import React, { useEffect, useRef, useMemo } from "react";
import useState from "react-usestateref";
import NeuroCareHeader from "../NeuroCareHeader";
import { db, patientDb, storage, functions } from "../firebase/firebase";
import { ref, child, get } from "firebase/database";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import {
  Button,
  Layout,
  Calendar,
  ConfigProvider,
  Input,
  Table,
  Modal,
  Badge,
  Select,
  Radio,
} from "antd";
import moment from "moment";
import "moment/locale/ko";
import locale from "antd/es/locale/ko_KR";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  query,
  where,
  collectionGroup,
} from "firebase/firestore";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { useAuth } from "../AuthProvider";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import Loader from "./Loader";

import arrowDownImg from "../imgs/arrow_drop_down.svg";
import expandLeftImg from "../imgs/expand_left.svg";
import expandRightImg from "../imgs/expand_right.svg";
import calendarDownImg from "../imgs/calendar_down.svg";
import checkedImg from "../imgs/check.svg";
import undoSelectedImg from "../imgs/undo_selected.svg";
import undoUnSelectedImg from "../imgs/undo_unselected.svg";
import redoSelectedImg from "../imgs/redo_selected.svg";
import redoUnSelectedImg from "../imgs/redo_unselected.svg";

import armImg from "../imgs/armImg.png";
import leftArrowGray from "../imgs/leftArmArrowGray.svg";
import leftArrowBlack from "../imgs/leftArmArrowBlack.svg";
import rightArrowGray from "../imgs/rightArmArrowGray.svg";
import rightArrowBlack from "../imgs/rightArmArrowBlack.svg";

import settingImg from "../imgs/setting_icon.png";

const getLabelByScore = (score) => {
  return "";
};

function scoreFromChangeValue(value, bestMax = 500, worstMax = 2000) {
  if (value <= 0) return 0;
  if (value <= bestMax) return 100;
  if (value >= worstMax) return 0;
  const score = (100 * (worstMax - value)) / (worstMax - bestMax);
  return Math.min(Math.max(score, 0), 100);
}

function scoreFromRange(value, bestMin, bestMax, worstMin, worstMax) {
  if (value >= bestMin && value <= bestMax) return 100;
  if (value < bestMin) {
    const score = (100 * (value - worstMin)) / (bestMin - worstMin);
    return Math.min(Math.max(score, 0), 100);
  }
  if (value > bestMax) {
    const score = (100 * (worstMax - value)) / (worstMax - bestMax);
    return Math.min(Math.max(score, 0), 100);
  }
  return 0;
}

function scoreFromGrowthValue(value, bestMin = 6, worstMin = 2) {
  if (value >= bestMin) return 100;
  if (value <= worstMin) return 0;
  const score = (100 * (value - worstMin)) / (bestMin - worstMin);
  return Math.min(Math.max(score, 0), 100);
}

function getAverage(array) {
  if (!Array.isArray(array) || array.length === 0) return 0;
  const sum = array.reduce((acc, val) => acc + val, 0);
  return sum / array.length;
}

function countUpperFive(arr) {
  return arr.filter((num) => num >= 5).length;
}

const CustomDot = (props) => {
  const { cx, cy, payload } = props;
  const numericValue = Number(payload.payload.score);
  const color =
    numericValue >= 75 ? "#3B8BD4" : numericValue >= 60 ? "#FAC775" : "#F09595";
  return (
    <circle
      cx={cx}
      cy={cy}
      r={4}
      fill="#FFFFFF"
      stroke={color}
      strokeWidth={2}
    />
  );
};

const thStyle = {
  border: "1px solid #E3E8EF",
  padding: "6px 10px",
  textAlign: "center",
  verticalAlign: "middle",
  backgroundColor: "#F8FAFC",
  color: "#818C9E",
  fontWeight: "600",
  fontSize: "11px",
};

const tdStyle = {
  border: "1px solid #E3E8EF",
  padding: "4px 10px",
  textAlign: "center",
  verticalAlign: "middle",
  color: "#818C9E",
  fontSize: "11px",
};

const tdLeft = {
  ...tdStyle,
  textAlign: "left",
};

const buttonStyle = {
  padding: "8px 16px",
  fontSize: "14px",
  backgroundColor: "#1677ff",
  color: "#fff",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
};

const isJointKey = (key) =>
  [
    "ShoulderLt",
    "ShoulderRt",
    "ElbowLt",
    "ElbowRt",
    "WristLt",
    "WristRt",
    "HipLt",
    "HipRt",
    "KneeLt",
    "KneeRt",
    "AnkleLt",
    "AnkleRt",
  ].includes(key);

const isReflexKey = (key) =>
  [
    "BicepsLt",
    "BicepsRt",
    "TricepsLt",
    "TricepsRt",
    "PatellarLt",
    "PatellarRt",
    "AchillesLt",
    "AchillesRt",
  ].includes(key);

const tableLabels = {
  Alertness: "Alertness",
  Orientation: "Orientation",
  Memory: "Memory",
  Speech: "Speech",
  Pupil: "Pupil size / Light reflex",
  EOM: "EOM / Nystagmus",
  Facial: "Facial sensory / motor",
  Gag: "Gag reflex / Tongue deviation",
  RUE: "RUE 근력",
  RLE: "RLE 근력",
  LUE: "LUE 근력",
  LLE: "LLE 근력",
  ShoulderLt: "Shoulder (어깨관절) Lt",
  ShoulderRt: "Shoulder (어깨관절) Rt",
  ElbowLt: "Elbow (팔꿈치관절) Lt",
  ElbowRt: "Elbow (팔꿈치관절) Rt",
  WristLt: "Wrist (손목관절) Lt",
  WristRt: "Wrist (손목관절) Rt",
  HipLt: "Hip (고관절) Lt",
  HipRt: "Hip (고관절) Rt",
  KneeLt: "Knee (무릎관절) Lt",
  KneeRt: "Knee (무릎관절) Rt",
  AnkleLt: "Ankle (발목관절) Lt",
  AnkleRt: "Ankle (발목관절) Rt",
  ToneUp: "Muscle tone(Up)",
  ToneLo: "Muscle tone(Lo)",
  SensRUE: "RUE 감각",
  SensRLE: "RLE 감각",
  SensLUE: "LUE 감각",
  SensLLE: "LLE 감각",
  DTR: "DTR",
  BicepsLt: "Biceps Reflex (상완이두근) Lt",
  BicepsRt: "Biceps Reflex (상완이두근) Rt",
  TricepsLt: "Triceps Reflex (상완삼두근) Lt",
  TricepsRt: "Triceps Reflex (상완삼두근) Rt",
  PatellarLt: "Patellar Reflex (슬개건) Lt",
  PatellarRt: "Patellar Reflex (슬개건) Rt",
  AchillesLt: "Achilles Reflex (아킬레스건) Lt",
  AchillesRt: "Achilles Reflex (아킬레스건) Rt",
  Babinski: "Babinski sign",
  Finger: "Finger-to-nose",
  Heel: "Heel-to-shin",
  Romberg: "Romberg's test",
  Walking: "Walking",
  Tandem: "Tandem gait",
};

const allKeys = {
  Alertness: "5",
  Orientation: "3",
  Memory: "정상",
  Speech: "Normal",
  Pupil: "Isocoria",
  EOM: "정상",
  Facial: "정상",
  Gag: "정상",
  RUE: "5",
  RLE: "5",
  LUE: "5",
  LLE: "5",
  ShoulderLt: "G0",
  ShoulderRt: "G0",
  ElbowLt: "G0",
  ElbowRt: "G0",
  WristLt: "G0",
  WristRt: "G0",
  HipLt: "G0",
  HipRt: "G0",
  KneeLt: "G0",
  KneeRt: "G0",
  AnkleLt: "G0",
  AnkleRt: "G0",
  ToneUp: "정상",
  ToneLo: "정상",
  SensRUE: "정상",
  SensRLE: "정상",
  SensLUE: "정상",
  SensLLE: "정상",
  DTR: "2+(정상)",
  BicepsLt: "2+(정상)",
  BicepsRt: "2+(정상)",
  TricepsLt: "2+(정상)",
  TricepsRt: "2+(정상)",
  PatellarLt: "2+(정상)",
  PatellarRt: "2+(정상)",
  AchillesLt: "2+(정상)",
  AchillesRt: "2+(정상)",
  Babinski: null,
  Finger: "정상",
  Heel: "정상",
  Romberg: "정상",
  Walking: "정상",
  Tandem: "정상",
};

const simpleAllKeys = {
  Alertness: "5",
  Orientation: "3",
  Memory: "정상",
  Speech: "Normal",
  Pupil: "Isocoria",
  EOM: "정상",
  Facial: "정상",
  Gag: "정상",
  RUE: "5",
  RLE: "5",
  LUE: "5",
  LLE: "5",
  ShoulderLt: "G0",
  ShoulderRt: "G0",
  ElbowLt: "G0",
  ElbowRt: "G0",
  WristLt: "G0",
  WristRt: "G0",
  HipLt: "G0",
  HipRt: "G0",
  KneeLt: "G0",
  KneeRt: "G0",
  AnkleLt: "G0",
  AnkleRt: "G0",
  ToneUp: "정상",
  ToneLo: "정상",
  DTR: "2+(정상)",
  Finger: "정상",
  Heel: "정상",
  Romberg: "정상",
  Walking: "정상",
  Tandem: "정상",
};

const Customer = () => {
  const { user, authReady } = useAuth();
  const [data, setData] = useState([]);
  const [patientList, setPatientList] = useState([]);
  const [selectedTest, setSelectedTest] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [filteredList, setFilteredList] = useState([]);
  const [currentList, setCurrentList] = useState([]);
  const [init, setInit] = useState(false);
  const [patientName, setPatientName] = useState("");
  const [patientNo, setPatientNo] = useState("");
  const [faceImage1, setFaceImage1] = useState("");
  const [faceImage2, setFaceImage2] = useState("");
  const [tongueImage, setTongueImage] = useState("");
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [reportData, setReportData] = useState({});
  const [activeExamType, setActiveExamType] = useState("general");
  const [guideCheck, setGuideCheck] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewScale, setPreviewScale] = useState(1);
  const [loadedMonths, setLoadedMonths] = useState(new Set());
  const [currentMonthView, setCurrentMonthView] = useState("");
  const [selectedDateView, setSelectedDateView] = useState(
    dayjs().format("YYYYMMDD"),
  );
  const [clientMapCache, setClientMapCache] = useState(null);
  const [bpDiastolic, setBpDiastolic] = useState("");
  const [bpSystolic, setBpSystolic] = useState("");
  const [showRemarks, setShowRemarks] = useState(false);
  const [remarksText, setRemarksText] = useState("");
  const [imageMarkers, setImageMarkers] = useState({});
  const [imageLines, setImageLines] = useState({});
  const [drawingLine, setDrawingLine] = useState(null);
  const [filterMode, setFilterMode] = useState("date");
  const [calendarMode, setCalendarMode] = useState("month");
  const [completionFilter, setCompletionFilter] = useState("all");
  const [appliedKeyword, setAppliedKeyword] = useState("");
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [armModalOpen, setArmModalOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // 확장검사 표시 여부. 마이페이지에서 PIN 등록 시 Info 문서에 기록되는
  // extendedWeb(true)을 읽어서 결정한다. 없거나 false면 확장검사 숨김.
  const [extendedActive, setExtendedActive] = useState(false);

  useEffect(() => {
    const checkExtendedConfirm = async () => {
      if (!user?.id) return;
      try {
        const infoSnap = await getDoc(doc(db, "Accounts", user.id, "Info", "data"));
        setExtendedActive(
          infoSnap.exists() && infoSnap.data().extendedWeb === true
        );
      } catch (error) {
        console.error("확장검사 활성 여부 확인 실패:", error);
      }
    };
    checkExtendedConfirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const [drawMode, setDrawMode] = useState(null);
  const [toastMsg, setToastMsg] = useState("");
  const toastTimerRef = useRef(null);
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const [isSaving, setIsSaving] = useState(false);
  const [saveToastVisible, setSaveToastVisible] = useState(false);
  const [copyToastVisible, setCopyToastVisible] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isReportAttached, setIsReportAttached] = useState(() => {
    const saved = localStorage.getItem("isReportAttached");
    return saved ? JSON.parse(saved) : false;
  });
  const [armChartView, setArmChartView] = useState("left");
  const [diseaseModalOpen, setDiseaseModalOpen] = useState(false);
  const [selectedDisease, setSelectedDisease] = useState(null);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyText, setCopyText] = useState("");

  const [settingModalOpen, setSettingModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem("viewMode");
    return saved ? JSON.parse(saved) : "full";
  });
  const [compactCriteria, setCompactCriteria] = useState(() => {
    const saved = localStorage.getItem("compactCriteria");
    return saved ? JSON.parse(saved) : "default";
  });
  const [checkAllBehavior, setCheckAllBehavior] = useState(() => {
    const saved = localStorage.getItem("checkAllBehavior");
    return saved ? JSON.parse(saved) : "fill_empty";
  });
  const [autoFillWithDisease, setAutoFillWithDisease] = useState(() => {
    const saved = localStorage.getItem("autoFillWithDisease");
    return saved ? JSON.parse(saved) : false;
  });

  const normalizeIncludedKeys = (parsed) => {
    if (!parsed || typeof parsed !== "object") {
      return { full: {}, compact: {} };
    }
    if ("full" in parsed || "compact" in parsed) {
      return { full: parsed.full || {}, compact: parsed.compact || {} };
    }

    return { full: { ...parsed }, compact: { ...parsed } };
  };

  const [customIncludedKeys, setCustomIncludedKeys] = useState(() => {
    const saved = localStorage.getItem("customIncludedKeys");
    return normalizeIncludedKeys(saved ? JSON.parse(saved) : null);
  });

  const handleCancelSettings = () => {
    const savedViewMode = localStorage.getItem("viewMode");
    setViewMode(savedViewMode ? JSON.parse(savedViewMode) : "full");

    const savedCriteria = localStorage.getItem("compactCriteria");
    setCompactCriteria(savedCriteria ? JSON.parse(savedCriteria) : "default");

    const savedBehavior = localStorage.getItem("checkAllBehavior");
    setCheckAllBehavior(savedBehavior ? JSON.parse(savedBehavior) : "fill_empty");

    const savedAuto = localStorage.getItem("autoFillWithDisease");
    setAutoFillWithDisease(savedAuto ? JSON.parse(savedAuto) : false);

    const savedKeys = localStorage.getItem("customIncludedKeys");
    setCustomIncludedKeys(
      normalizeIncludedKeys(savedKeys ? JSON.parse(savedKeys) : null),
    );

    setSettingModalOpen(false);
  };

  const getVisibleKeys = () => {
    const isSimple = activeExamType === "simple";
    const baseKeys = isSimple ? simpleAllKeys : allKeys;

    const defaultCompactItems = [
      "Alertness", "Orientation", "Memory", "Speech",
      "Pupil", "EOM", "Facial", "Gag",
      "RUE", "RLE", "LUE", "LLE", "ToneUp", "ToneLo",
      "SensRUE", "SensRLE", "SensLUE", "SensLLE",
      "DTR", "Babinski",
      "Finger", "Heel", "Romberg",
      "Walking", "Tandem",
    ];

    let baseForMode = {};
    if (viewMode === "full") {

      Object.keys(baseKeys).forEach((key) => {
        if (!isSimple && key === "DTR") return;
        baseForMode[key] = baseKeys[key];
      });
    } else {

      Object.keys(baseKeys).forEach((key) => {
        if (defaultCompactItems.includes(key))
          baseForMode[key] = baseKeys[key];
      });
    }

    if (compactCriteria === "custom") {
      const modeMap = customIncludedKeys[viewMode] || {};
      const visibleKeys = {};
      Object.keys(baseForMode).forEach((key) => {
        if (modeMap[key] !== false) visibleKeys[key] = baseForMode[key];
      });
      return visibleKeys;
    }

    return baseForMode;
  };

  const diseaseCodes = useMemo(
    () => [
      { code: "R42", desc: "현기증 어지러움" },
      { code: "R51", desc: "두통" },
      { code: "R47.1", desc: "구음장애" },
      { code: "R29.6", desc: "경향성 낙상" },
      { code: "I63", desc: "뇌경색증" },
      { code: "G45", desc: "일과성 대뇌허혈발작 (TIA)" },
      { code: "S06", desc: "두개내손상 (외상성 뇌손상 / TBI)" },
      { code: "C71", desc: "뇌의 악성 신생물 (뇌종양)" },
      { code: "G20/G51.0", desc: "파킨슨병 / 벨마비" },
    ],
    [],
  );

  const showToast = (msg) => {
    setToastMsg(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastMsg(""), 2500);
  };

  const handleDrawModeChange = (mode) => {
    if (drawMode === mode) {
      setDrawMode(null);
      return;
    }
    setDrawMode(mode);
    if (mode === "check") showToast("클릭하여 체크 표시를 추가하세요");
    else if (mode === "line")
      showToast("시작점 클릭 -> 끝점 클릭으로 거리와 각도 측정");
  };

  const saveHistory = () => {
    setHistory((prev) => [
      ...prev,
      {
        markers: imageMarkers[previewImage]
          ? [...imageMarkers[previewImage]]
          : [],
        lines: imageLines[previewImage] ? [...imageLines[previewImage]] : [],
      },
    ]);
    setRedoStack([]);
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    const lastState = history[history.length - 1];
    const currentState = {
      markers: imageMarkers[previewImage]
        ? [...imageMarkers[previewImage]]
        : [],
      lines: imageLines[previewImage] ? [...imageLines[previewImage]] : [],
    };
    setRedoStack((prev) => [...prev, currentState]);
    setImageMarkers((prev) => ({ ...prev, [previewImage]: lastState.markers }));
    setImageLines((prev) => ({ ...prev, [previewImage]: lastState.lines }));
    setHistory((prev) => prev.slice(0, -1));
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const nextState = redoStack[redoStack.length - 1];
    const currentState = {
      markers: imageMarkers[previewImage]
        ? [...imageMarkers[previewImage]]
        : [],
      lines: imageLines[previewImage] ? [...imageLines[previewImage]] : [],
    };
    setHistory((prev) => [...prev, currentState]);
    setImageMarkers((prev) => ({ ...prev, [previewImage]: nextState.markers }));
    setImageLines((prev) => ({ ...prev, [previewImage]: nextState.lines }));
    setRedoStack((prev) => prev.slice(0, -1));
  };

  const handleClearAll = () => {
    saveHistory();
    setImageMarkers((prev) => ({ ...prev, [previewImage]: [] }));
    setImageLines((prev) => ({ ...prev, [previewImage]: [] }));
  };

  const fetchMonthData = async (targetYYYYMM, forceReload = false) => {
    if (!forceReload && loadedMonths.has(targetYYYYMM)) return;
    try {
      setLoading(true);
      let currentClientMap = clientMapCache;
      if (!currentClientMap) {
        const clientsSnap = await get(
          child(ref(patientDb), `Account/${user.id}/Clients`),
        );
        currentClientMap = {};
        clientsSnap.forEach((c) => {
          const v = c.val();
          currentClientMap[v.code] = v.name;
        });
        setClientMapCache(currentClientMap);
      }

      // 보관함 code 중 명부에 재등록되지 않은 것만 제외
      const deletedCodes = new Set();
      const deletedSnap = await get(
        child(ref(patientDb), `Account/${user.id}/DeletedClients`),
      );
      deletedSnap.forEach((c) => {
        const dCode = String(c.val()?.code ?? "").trim();
        if (dCode && !(dCode in currentClientMap)) deletedCodes.add(dCode);
      });

      const patientsSnap = await getDocs(
        collection(db, "Accounts", user.id, "Patients"),
      );
      const patientDataMap = {};
      patientsSnap.forEach((doc) => {
        patientDataMap[doc.id] = doc.data();
      });

      const testsQuery = query(
        collectionGroup(db, "Tests"),
        where("hospitalId", "==", user.id),
        where("createdAt", ">=", `${targetYYYYMM}01`),
        where("createdAt", "<=", `${targetYYYYMM}31\uf8ff`),
      );

      const testsSnap = await getDocs(testsQuery);
      const newPatientList = [];
      const testsByPatient = {};

      testsSnap.forEach((doc) => {
        const testData = doc.data();
        const patientId = testData.patientId;
        // 보관함(소프트 삭제) 환자의 검사는 목록에서 제외
        if (deletedCodes.has(String(patientId ?? "").trim())) return;
        const dateTime = doc.id;
        const date = String(dateTime).split("-")[0];
        const name = currentClientMap[patientId] || "이름 없음";
        const isChecked = testData.isChecked === true;
        newPatientList.push({ date, dateTime, patientId, name, isChecked });

        if (!testsByPatient[patientId]) {
          testsByPatient[patientId] = [];
        }
        testsByPatient[patientId].push({
          id: dateTime,
          ...testData,
        });
      });

      const newFullData = Object.keys(testsByPatient).map((patientId) => {
        return {
          id: patientId,
          name: currentClientMap[patientId] || "이름 없음",
          ...(patientDataMap[patientId] || {}),
          tests: testsByPatient[patientId],
        };
      });

      setPatientList((prev) => {
        let merged = forceReload
          ? [
              ...prev.filter((p) => !p.date.startsWith(targetYYYYMM)),
              ...newPatientList,
            ]
          : [...prev, ...newPatientList];
        return Array.from(
          new Map(merged.map((item) => [item.dateTime, item])).values(),
        );
      });

      setData((prev) => {
        const dataMap = new Map();
        prev.forEach((p) => dataMap.set(p.id, p));
        newFullData.forEach((p) => {
          if (dataMap.has(p.id)) {
            const existing = dataMap.get(p.id);
            const mergedTests = forceReload
              ? [
                  ...existing.tests.filter(
                    (t) => !t.id.startsWith(targetYYYYMM),
                  ),
                  ...p.tests,
                ]
              : [...existing.tests, ...p.tests];
            const uniqueTests = Array.from(
              new Map(mergedTests.map((t) => [t.id, t])).values(),
            );
            dataMap.set(p.id, { ...existing, tests: uniqueTests });
          } else {
            dataMap.set(p.id, p);
          }
        });
        return Array.from(dataMap.values());
      });

      setLoadedMonths((prev) => new Set(prev).add(targetYYYYMM));
      setLoading(false);
    } catch (err) {
      console.error("월별 검사 데이터 로드 실패:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    let baseList = [];
    if (filterMode === "month") {
      baseList = patientList.filter((item) =>
        item.date.startsWith(currentMonthView),
      );
    } else {
      baseList = patientList.filter((item) => item.date === selectedDateView);
    }

    setCurrentList([...baseList]);

    let fPatientList = [...baseList];

    if (completionFilter === "complete") {
      fPatientList = fPatientList.filter((item) => item.isChecked);
    } else if (completionFilter === "incomplete") {
      fPatientList = fPatientList.filter((item) => !item.isChecked);
    }

    if (appliedKeyword) {
      fPatientList = fPatientList.filter((item) =>
        (item.name || "").includes(appliedKeyword),
      );
    }
    fPatientList.sort((a, b) => b.dateTime.localeCompare(a.dateTime));

    setFilteredList(fPatientList);
  }, [
    patientList,
    selectedDateView,
    currentMonthView,
    filterMode,
    completionFilter,
    appliedKeyword,
  ]);

  useEffect(() => {
    if (!init && user && authReady) {
      setInit(true);
      const thisMonth = dayjs().format("YYYYMM");
      setCurrentMonthView(thisMonth);
      fetchMonthData(thisMonth);
    }
  }, [user, init, authReady]);

  useEffect(() => {
    setIsAllChecked(false);
    if (selectedTest) {
      setPatientName(selectedTest.name || "");
      setPatientNo(selectedTest.patientId || "");
      if (selectedTest.reports) {
        setReportData(selectedTest.reports);
      } else {
        setReportData({});
      }
      setRemarksText(selectedTest.note || "");
      setBpDiastolic(selectedTest.bpDiastolic || "");
      setBpSystolic(selectedTest.bpSystolic || "");
      setSelectedDisease(selectedTest.disease || null);
    } else {
      setPatientName("");
      setPatientNo("");
      setReportData({});
      setRemarksText("");
      setBpDiastolic("");
      setBpSystolic("");
      setSelectedDisease(null);
    }
  }, [selectedTest?.id]);

  useEffect(() => {
    let cancelled = false;

    setFaceImage1("");
    setFaceImage2("");
    setTongueImage("");
    setImageMarkers({});
    setImageLines({});
    setImagesLoaded(false);

    const loadStorageImages = async () => {
      if (!selectedTest) return;
      try {
        const defaultPath = `gs://neurocare-8a9f4.firebasestorage.app/${user.id}/${selectedTest.patientId}/${selectedTest.createdAt}`;
        const facePath1 = `${defaultPath}/expressionless.jpg`;
        const facePath2 = `${defaultPath}/smile.jpg`;
        const tonguePath = `${defaultPath}/tongue.jpg`;

        const [url1, url2, url3] = await Promise.all([
          getDownloadURL(storageRef(storage, facePath1)).catch(() => ""),
          getDownloadURL(storageRef(storage, facePath2)).catch(() => ""),
          getDownloadURL(storageRef(storage, tonguePath)).catch(() => ""),
        ]);

        if (cancelled) return;

        setFaceImage1(url1);
        setFaceImage2(url2);
        setTongueImage(url3);

        const savedAnnotations = selectedTest.annotations || {};
        const newMarkers = {};
        const newLines = {};

        if (url1 && savedAnnotations.expressionless) {
          newMarkers[url1] = savedAnnotations.expressionless.markers || [];
          newLines[url1] = savedAnnotations.expressionless.lines || [];
        }

        if (url2 && savedAnnotations.smile) {
          newMarkers[url2] = savedAnnotations.smile.markers || [];
          newLines[url2] = savedAnnotations.smile.lines || [];
        }

        if (url3 && savedAnnotations.tongue) {
          newMarkers[url3] = savedAnnotations.tongue.markers || [];
          newLines[url3] = savedAnnotations.tongue.lines || [];
        }

        setImageMarkers(newMarkers);
        setImageLines(newLines);
        setImagesLoaded(true);
      } catch (error) {
        console.error("이미지 및 주석 데이터 로드 실패:", error);
      }
    };

    loadStorageImages();

    if (selectedTest) {
      setRemarksText(selectedTest.note || "");
      setBpDiastolic(selectedTest.bpDiastolic || "");
      setBpSystolic(selectedTest.bpSystolic || "");
      if (selectedTest.reports) {
        setReportData(selectedTest.reports);
      }
    }

    return () => {
      cancelled = true;
    };
  }, [selectedTest?.id, user.id]);

  let avgAvgRatio = 0;
  let avgEyeRatio = 0;
  let avgMouthRatio = 0;
  let avgPupilData = 0;
  let pass_count = 0;
  let total_count = 0;
  let avgMotorData = 0;
  let leftAngleAvgData = 0;
  let rightAngleAvgData = 0;
  let avgSpeechData = 0;
  let speechAccuracy = 0;
  let speechFluency = 0;
  let speechCompleteness = 0;

  let leftAngleMinData = 0;
  let leftAngleMaxData = 0;
  let rightAngleMinData = 0;
  let rightAngleMaxData = 0;

  let leftMotorGrade = 0;
  let rightMotorGrade = 0;

  let checkPupilData = 0;
  let checkSpeechData = 0;
  let checkAvgRatio = 0;
  let checkMotorData = 0;

  let rombergStatus = 1;

  let leftArmChartData = [];
  let rightArmChartData = [];
  let chartMaxX = 10;
  let xTicks = [];

  let isSmileText = "Non Smile";
  let leftFailCount = 0;
  let rightFailCount = 0;

  let isFaceNonTest = false;
  let isPupilNonTest = false;
  let isLeftMotorNonTest = false;
  let isRightMotorNonTest = false;
  let isSpeechNonTest = false;

  if (selectedTest) {
    const test = selectedTest;
    if (test.face_data) {
      const firstEyeratio = test.face_data.first?.firstEyeratio || 0;
      const firstMouthratio = test.face_data.first?.firstMouthratio || 0;
      const lastEyeratio = test.face_data.last?.lastEyeratio || 0;
      const lastMouthratio = test.face_data.last?.lastMouthratio || 0;

      const firstSmile = test.face_data.first?.firstSmileIntensityAvg || 0;
      const lastSmile = test.face_data.last?.lastSmileIntensityAvg || 0;

      avgEyeRatio = Math.round(((firstEyeratio + lastEyeratio) / 2) * 10) / 10;
      avgMouthRatio =
        Math.round(((firstMouthratio + lastMouthratio) / 2) * 10) / 10;

      if (lastSmile - firstSmile >= 0.2) {
        isSmileText = "Smile";
      } else {
        isSmileText = "Non Smile";
      }
    }
    if (test.pupil_data) {
      pass_count = test.pupil_data.pass_count || 0;
      total_count = test.pupil_data.total_count || 0;
    }

    const trials = test.pupil_trials || test.pupil_data?.pupil_trials || [];
    trials.forEach((trial) => {
      if (trial.result_type && trial.result_type.includes("FAIL")) {
        if (trial.target_direction === "LEFT") leftFailCount++;
        if (trial.target_direction === "RIGHT") rightFailCount++;
      }
    });

    if (test.moter_data) {
      leftAngleAvgData = getAverage(test.moter_data.left_arm_angles || []);
      rightAngleAvgData = getAverage(test.moter_data.right_arm_angles || []);
      const leftArr = [...(test.moter_data.left_arm_angles || [])];
      const rightArr = test.moter_data.right_arm_angles || [];
      const shouldersArr = test.moter_data.shoulder_angles || [];

      if (shouldersArr.length > 0) {
        const shMin = Math.min(...shouldersArr);
        const shMax = Math.max(...shouldersArr);
        const shVariation = shMax - shMin;
        if (shVariation < 10) {
          rombergStatus = 1;
        } else {
          rombergStatus = 0;
        }
      }
      if (leftArr.length > 0) {
        leftAngleMinData = Math.min(...leftArr);
        leftAngleMaxData = Math.max(...leftArr);

        let drop15Time = 999;
        let drop60Time = 999;
        let currentMax = leftArr[0];
        for (let i = 0; i < leftArr.length; i++) {
          if (leftArr[i] > currentMax) currentMax = leftArr[i];
          if (drop15Time === 999 && currentMax - leftArr[i] >= 15)
            drop15Time = i;
          if (drop60Time === 999 && currentMax - leftArr[i] >= 60)
            drop60Time = i;
        }

        const maxAngle = leftAngleMaxData;
        const maxVariation = leftAngleMaxData - leftAngleMinData;

        if (maxAngle < 30 && maxVariation < 5) leftMotorGrade = 0;
        else if (
          (maxAngle < 30 && maxVariation >= 5) ||
          (maxAngle >= 30 && drop60Time <= 1.0)
        )
          leftMotorGrade = 1;
        else if (
          maxAngle >= 30 &&
          drop60Time > 1.0 &&
          maxVariation >= 15 &&
          drop15Time <= 2.0
        )
          leftMotorGrade = 2;
        else if (
          (maxAngle >= 30 &&
            drop60Time > 1.0 &&
            maxVariation >= 15 &&
            drop15Time > 2.0) ||
          (maxAngle >= 30 &&
            maxAngle < 70 &&
            drop60Time > 1.0 &&
            maxVariation < 15)
        )
          leftMotorGrade = 3;
        else if (
          maxAngle >= 70 &&
          drop60Time > 1.0 &&
          maxVariation >= 5 &&
          maxVariation < 15
        )
          leftMotorGrade = 4;
        else if (maxAngle >= 70 && drop60Time > 1.0 && maxVariation < 5)
          leftMotorGrade = 5;
        else leftMotorGrade = 0;
      }

      if (rightArr.length > 0) {
        rightAngleMinData = Math.min(...rightArr);
        rightAngleMaxData = Math.max(...rightArr);

        let drop15Time = 999;
        let drop60Time = 999;
        let currentMax = rightArr[0];
        for (let i = 0; i < rightArr.length; i++) {
          if (rightArr[i] > currentMax) currentMax = rightArr[i];
          if (drop15Time === 999 && currentMax - rightArr[i] >= 15)
            drop15Time = i;
          if (drop60Time === 999 && currentMax - rightArr[i] >= 60)
            drop60Time = i;
        }

        const maxAngle = rightAngleMaxData;
        const maxVariation = rightAngleMaxData - rightAngleMinData;

        if (maxAngle < 30 && maxVariation < 5) rightMotorGrade = 0;
        else if (
          (maxAngle < 30 && maxVariation >= 5) ||
          (maxAngle >= 30 && drop60Time <= 1.0)
        )
          rightMotorGrade = 1;
        else if (
          maxAngle >= 30 &&
          drop60Time > 1.0 &&
          maxVariation >= 15 &&
          drop15Time <= 2.0
        )
          rightMotorGrade = 2;
        else if (
          (maxAngle >= 30 &&
            drop60Time > 1.0 &&
            maxVariation >= 15 &&
            drop15Time > 2.0) ||
          (maxAngle >= 30 &&
            maxAngle < 70 &&
            drop60Time > 1.0 &&
            maxVariation < 15)
        )
          rightMotorGrade = 3;
        else if (
          maxAngle >= 70 &&
          drop60Time > 1.0 &&
          maxVariation >= 5 &&
          maxVariation < 15
        )
          rightMotorGrade = 4;
        else if (maxAngle >= 70 && drop60Time > 1.0 && maxVariation < 5)
          rightMotorGrade = 5;
        else rightMotorGrade = 0;
      }

      const formatData = (arr) => {
        if (!arr || arr.length === 0) return [];
        return arr.map((val, i) => ({
          time: i,
          angle: isNaN(val) ? 0 : Number(val),
        }));
      };

      leftArmChartData = formatData(leftArr);
      rightArmChartData = formatData(rightArr);

      const maxLen = Math.max(leftArr.length, rightArr.length);

      chartMaxX = maxLen > 0 ? maxLen - 1 : 0;

      for (let i = 0; i <= chartMaxX; i += 5) {
        xTicks.push(i);
      }

      if (chartMaxX % 5 !== 0 && chartMaxX !== 0) {
        xTicks.push(chartMaxX);
      }
    }
    if (test.speach_data) {
      speechAccuracy = Math.floor(
        test.speach_data.AccuracyScore || test.speach_data.Accuracy || 0,
      );
      speechFluency = Math.floor(test.speach_data.FluencyScore || 0);
      speechCompleteness = Math.floor(test.speach_data.CompletenessScore || 0);
    }
    if (test.total_data) {
      if (test.total_data.face_score) {
        avgAvgRatio = Number(test.total_data.face_score.toFixed(1));
        checkAvgRatio = avgAvgRatio;
      } else {
        checkAvgRatio = 0;
      }
      if (test.total_data.motor_score) {
        avgMotorData = Number(test.total_data.motor_score.toFixed(1));
        checkMotorData = avgMotorData;
      } else {
        checkMotorData = 0;
      }
      if (test.total_data.pupil_score) {
        avgPupilData = Number(test.total_data.pupil_score.toFixed(1));
        checkPupilData = avgPupilData;
      } else {
        checkPupilData = 0;
      }
      if (test.total_data.speech_score) {
        avgSpeechData = Number(test.total_data.speech_score.toFixed(1));
        checkSpeechData = avgSpeechData;
      } else {
        checkSpeechData = 0;
      }
    }

    isFaceNonTest =
      !test.face_data || (avgEyeRatio === 0 && avgMouthRatio === 0);
    isPupilNonTest =
      (!test.pupil_data || total_count === 0) &&
      leftFailCount === 0 &&
      rightFailCount === 0;
    isLeftMotorNonTest =
      !test.moter_data ||
      !test.moter_data.left_arm_angles ||
      test.moter_data.left_arm_angles.length === 0;
    isRightMotorNonTest =
      !test.moter_data ||
      !test.moter_data.right_arm_angles ||
      test.moter_data.right_arm_angles.length === 0;
    isSpeechNonTest =
      avgSpeechData === 0 &&
      speechAccuracy === 0 &&
      speechFluency === 0 &&
      speechCompleteness === 0;
  }

  const currentChartData =
    armChartView === "left" ? leftArmChartData : rightArmChartData;
  const currentLineColor = armChartView === "left" ? "#5B7BE8CC" : "#3AAA7ACC";
  const currentFillColor = armChartView === "left" ? "#5B7BE81A" : "#3AAA7A1A";
  const currentBadgeText =
    armChartView === "left" ? "왼팔 각도" : "오른팔 각도";

  const radarChartElement = useMemo(() => {
    if (!selectedTest) return null;

    const radarData = [
      { subject: "동공운동", score: avgPupilData, fullMark: 100 },
      { subject: "상관대칭", score: avgEyeRatio, fullMark: 100 },
      { subject: "하관대칭", score: avgMouthRatio, fullMark: 100 },
      { subject: "언어능력", score: avgSpeechData, fullMark: 100 },
      {
        subject: "오른팔편측",
        score: rightMotorGrade > -1 ? rightMotorGrade * 20 : 0,
        fullMark: 100,
      },
      {
        subject: "왼팔편측",
        score: leftMotorGrade > -1 ? leftMotorGrade * 20 : 0,
        fullMark: 100,
      },
    ];

    const LABEL_OFFSET = 15;
    const FONT_MAIN = 12;
    const FONT_SUB = 11;
    const renderCustomTick = (props) => {
      const { cx, cy, x, y, payload, index } = props;
      const item = radarData[index];
      const dx = x - cx;
      const dy = y - cy;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      const labelX = x + (dx / len) * LABEL_OFFSET;
      const labelY = y + (dy / len) * LABEL_OFFSET;
      const isTop = labelY < cy;

      let displayValue = `${Math.floor(item.score)}%`;
      if (item.subject === "왼팔편측")
        displayValue = isLeftMotorNonTest
          ? `GRADE -`
          : `GRADE ${leftMotorGrade}`;
      if (item.subject === "오른팔편측")
        displayValue = isRightMotorNonTest
          ? `GRADE -`
          : `GRADE ${rightMotorGrade}`;

      return (
        <g transform={`translate(${labelX},${labelY})`}>
          <text
            textAnchor="middle"
            dy={isTop ? -6 : 0}
            fontSize={FONT_MAIN}
            fill="#64748B"
            fontWeight="600"
          >
            {payload.value}
          </text>
          <text
            textAnchor="middle"
            dy={isTop ? 5 : 11}
            fontSize={FONT_SUB}
            fill="#1E293B"
            fontWeight="bold"
          >
            {displayValue}
          </text>
        </g>
      );
    };

    return (
      <>
        <ResponsiveContainer width={"100%"} height={280}>
          <RadarChart cx={150} cy={140} outerRadius={90} data={radarData}>
            <PolarGrid />
            <PolarAngleAxis dataKey="subject" tick={renderCustomTick} />
            <Radar
              name="점수"
              dataKey="score"
              stroke="#2C6AFF"
              fill="#C5DEFF"
              fillOpacity={0.4}
              dot={<CustomDot />}
              isAnimationActive={false}
            />
          </RadarChart>
        </ResponsiveContainer>
      </>
    );
  }, [
    selectedTest,
    avgEyeRatio,
    avgPupilData,
    avgSpeechData,
    avgMouthRatio,
    leftMotorGrade,
    rightMotorGrade,
    isLeftMotorNonTest,
    isRightMotorNonTest,
  ]);

  const validateReportData = () => {
    const currentAllowedKeys = getVisibleKeys();

    for (const key of Object.keys(currentAllowedKeys)) {
      const itemData = reportData[key];
      if (!itemData) continue;

      if (itemData.normal || itemData.abnormal) {
        if (key === "Babinski") continue;

        if (isJointKey(key)) {
          if (itemData.abnormal && (!itemData.type || !itemData.score)) {
            alert(
              `${tableLabels[key] || key} 항목의 점수/등급을 모두 체크해주세요.`,
            );
            return false;
          }
        } else {
          if (!itemData.score) {
            alert(
              `${tableLabels[key] || key} 항목의 점수/등급을 체크해주세요.`,
            );
            return false;
          }
        }
      }
    }
    return true;
  };

  const renderJointScoreCell = (key) => {
    const current = reportData[key] || {};
    const isNormal = current.normal || false;
    const isAbnormal = current.abnormal || false;
    const disabled = !(isNormal || isAbnormal);
    const themeColor = isNormal ? "blue" : isAbnormal ? "red" : "black";

    const handleTypeClick = (type) => {
      if (!isAbnormal) return;
      setReportData((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          type: prev[key]?.type === type ? null : type,
        },
      }));
    };

    const handleScoreClick = (score) => {
      if (disabled) return;
      if (isNormal && score !== "G0") return;
      if (isAbnormal && score === "G0") return;

      setReportData((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          score: prev[key]?.score === score ? null : score,
        },
      }));
    };

    const renderMiniCheck = (label, isChecked, onClick, isDisabled) => {
      const bg = isChecked
        ? isDisabled
          ? "#94A3B8"
          : themeColor
        : "transparent";
      return (
        <label
          key={label}
          style={{
            display: "inline-flex",
            alignItems: "center",
            marginRight: "8px",
            cursor: isDisabled ? "not-allowed" : "pointer",
            opacity: isDisabled ? 0.4 : 1,
            userSelect: "none",
          }}
          onClick={(e) => {
            e.preventDefault();
            onClick();
          }}
        >
          <span
            style={{
              display: "inline-flex",
              width: "12px",
              height: "12px",
              borderRadius: "4px",
              border: `1px solid ${themeColor}`,
              marginRight: "4px",
              backgroundColor: bg,
              alignItems: "center",
              justifyContent: "center",
              color: isChecked ? "white" : "transparent",
              fontSize: "10px",
            }}
          >
            ✓
          </span>
          <span style={{ fontSize: "11px", color: "#364153" }}>{label}</span>
        </label>
      );
    };

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "2px",
          textAlign: "left",
          paddingLeft: "10px",
        }}
      >
        <span
          style={{
            fontSize: "11px",
            fontWeight: "600",
            marginRight: "4px",
            color: "#64748B",
          }}
        >
          유형:
        </span>
        {["Spasticity", "Rigidity", "Hypotonia"].map((t) =>
          renderMiniCheck(
            t,
            current.type === t,
            () => handleTypeClick(t),
            isNormal || disabled,
          ),
        )}
        <span
          style={{
            fontSize: "11px",
            fontWeight: "600",
            margin: "0 6px",
            color: "#64748B",
          }}
        >
          / MAS:
        </span>
        {["G0", "G1", "G1+", "G2", "G3", "G4"].map((s) => {
          const isScoreDisabled =
            disabled || (isNormal && s !== "G0") || (isAbnormal && s === "G0");
          return renderMiniCheck(
            s,
            current.score === s,
            () => handleScoreClick(s),
            isScoreDisabled,
          );
        })}
      </div>
    );
  };

  const buildGuideResult = () => {
    const result = {};
    if (checkPupilData >= 90) {
      result["Alertness"] = { normal: true, abnormal: false, score: "5" };
    } else if (checkPupilData >= 80) {
      result["Alertness"] = { normal: false, abnormal: true, score: "4" };
    } else if (checkPupilData >= 70) {
      result["Alertness"] = { normal: false, abnormal: true, score: "3" };
    } else if (checkPupilData >= 60) {
      result["Alertness"] = { normal: false, abnormal: true, score: "2" };
    } else {
      result["Alertness"] = { normal: false, abnormal: true, score: "1" };
    }

    if (checkSpeechData >= 85) {
      result["Speech"] = { normal: true, abnormal: false, score: "Normal" };
    } else if (checkSpeechData >= 51) {
      result["Speech"] = { normal: false, abnormal: true, score: "Motor" };
    } else if (checkSpeechData > 0) {
      result["Speech"] = { normal: false, abnormal: true, score: "Dysarthria" };
    } else {
      result["Speech"] = { normal: false, abnormal: true, score: "Sensory" };
    }

    if (checkPupilData >= 70) {
      result["EOM"] = { normal: true, abnormal: false, score: "정상" };
    } else {
      result["EOM"] = { normal: false, abnormal: true, score: "제한" };
    }

    if (checkAvgRatio >= 85) {
      result["Facial"] = { normal: true, abnormal: false, score: "정상" };
    } else if (avgAvgRatio >= 50) {
      result["Facial"] = { normal: false, abnormal: true, score: "감각저하" };
    } else {
      result["Facial"] = { normal: false, abnormal: true, score: "마비" };
    }

    result["LUE"] = {
      normal: leftMotorGrade >= 4,
      abnormal: leftMotorGrade < 4,
      score: String(leftMotorGrade),
    };
    result["RUE"] = {
      normal: rightMotorGrade >= 4,
      abnormal: rightMotorGrade < 4,
      score: String(rightMotorGrade),
    };

    result["Romberg"] = {
      normal: rombergStatus === 1,
      abnormal: rombergStatus === 0,
      score: rombergStatus === 1 ? "정상" : "흔들림",
    };
    return result;
  };

  const guideCheckChanged = (e) => {
    const checked = e.target.checked;
    setGuideCheck(checked);
    if (checked) {
      const result = buildGuideResult();
      setReportData((prev) => ({ ...prev, ...result }));
    }
  };

  const renderNeuroTable = () => {
    const handleCheck = (key, type) => {
      setReportData((prev) => {
        const current = prev[key] || {};
        let newNormal = current.normal || false;
        let newAbnormal = current.abnormal || false;

        let shouldResetScore = false;

        if (type === "normal") {
          if (!newNormal && current.abnormal) {
            shouldResetScore = true;
          }
          newNormal = !newNormal;
          if (newNormal) newAbnormal = false;
        } else if (type === "abnormal") {
          if (!newAbnormal && current.normal) {
            shouldResetScore = true;
          }
          newAbnormal = !newAbnormal;
          if (newAbnormal) newNormal = false;
        }

        if (!newNormal && !newAbnormal) {
          shouldResetScore = true;
        }

        let newScore = shouldResetScore ? null : current.score;
        let newType = shouldResetScore ? null : current.type;

        if (isJointKey(key)) {
          if (newNormal) {
            newScore = "G0";
            newType = null;
          } else if (newAbnormal) {
            if (newScore === "G0") newScore = null;
          } else {
            newScore = null;
            newType = null;
          }
        } else if (isReflexKey(key) || key === "DTR") {
          if (newNormal) {
            newScore = "2+(정상)";
          } else if (newAbnormal) {
            if (newScore === "2+(정상)") newScore = null;
          } else {
            newScore = null;
          }
        }

        return {
          ...prev,
          [key]: {
            normal: newNormal,
            abnormal: newAbnormal,
            score: newScore,
            type: newType,
          },
        };
      });
    };

    const handleScoreSelect = (key, score) => {
      setReportData((prev) => {
        const current = prev[key] || {
          normal: false,
          abnormal: false,
          score: null,
        };
        if (isReflexKey(key) || key === "DTR") {
          if (current.normal && score !== "2+(정상)") return prev;
          if (current.abnormal && score === "2+(정상)") return prev;
        }
        return {
          ...prev,
          [key]: { ...current, score: current.score === score ? null : score },
        };
      });
    };

    const scoreCheckbox = (key, score) => {
      const isNormal = reportData[key]?.normal;
      const isAbnormal = reportData[key]?.abnormal;
      const isChecked = reportData[key]?.score === score;
      const borderColor = isNormal ? "blue" : isAbnormal ? "red" : "black";
      const backgroundColor = isChecked ? borderColor : "transparent";
      const disabled = !(isNormal || isAbnormal);

      return (
        <label
          key={score}
          style={{
            display: "inline-block",
            marginRight: "6px",
            cursor: disabled ? "not-allowed" : "pointer",
            userSelect: "none",
            lineHeight: "16px",
            opacity: disabled ? 0.4 : 1,
          }}
          onClick={() => {
            if (!disabled) handleScoreSelect(key, score);
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: "16px",
              height: "16px",
              borderRadius: "4px",
              backgroundColor: backgroundColor,
              border: `1px solid black`,
              position: "relative",
              verticalAlign: "middle",
              marginRight: "4px",
              color: isChecked ? "white" : "transparent",
            }}
          >
            <input
              type="checkbox"
              checked={isChecked}
              readOnly
              style={{
                opacity: 0,
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                margin: 0,
                cursor: disabled ? "not-allowed" : "pointer",
              }}
            />
            {isChecked ? "✓" : ""}
          </span>
          <span style={{ verticalAlign: "middle" }}>{score}</span>
        </label>
      );
    };

    const renderCustomCheckbox = (checked, onChange, color = "black") => (
      <span
        style={{
          display: "inline-flex",
          justifyContent: "center",
          alignItems: "center",
          width: "16px",
          height: "16px",
          borderRadius: "4px",
          backgroundColor: checked ? color : "transparent",
          border: `1px solid black`,
          position: "relative",
          verticalAlign: "middle",
          cursor: "pointer",
          color: checked ? "white" : "transparent",
        }}
        onClick={onChange}
      >
        <input
          type="checkbox"
          checked={checked}
          readOnly
          style={{
            opacity: 0,
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            margin: 0,
            cursor: "pointer",
          }}
        />
        {checked ? "✓" : ""}
      </span>
    );

    const checkPair = (key) => (
      <>
        <td style={tdStyle}>
          {renderCustomCheckbox(
            reportData[key]?.normal || false,
            () => handleCheck(key, "normal"),
            "blue",
          )}
        </td>
        <td style={tdStyle}>
          {renderCustomCheckbox(
            reportData[key]?.abnormal || false,
            () => handleCheck(key, "abnormal"),
            "red",
          )}
        </td>
      </>
    );

    const renderGroup = (groupTitle, items) => {
      const visibleKeys = getVisibleKeys();
      const filteredItems = items.filter((item) => {
        if (item.customCheck) {
          if (item.key && !(item.key in visibleKeys)) return false;
        }
        return item.key in visibleKeys;
      });

      if (filteredItems.length === 0) return null;

      return (
        <>
          <tr>
            <td
              rowSpan={filteredItems.length}
              style={{
                ...tdStyle,
                color: "#364153",
                fontWeight: "600",
              }}
            >
              {groupTitle}
            </td>
            {filteredItems[0].customCheck ? (
              filteredItems[0].customCheck
            ) : (
              <>
                <td style={tdLeft}>{filteredItems[0].label}</td>
                {checkPair(filteredItems[0].key)}
                <td
                  style={{
                    ...tdStyle,
                    textAlign: filteredItems[0].customScoreCell
                      ? "left"
                      : "center",
                  }}
                >
                  {filteredItems[0].customScoreCell
                    ? filteredItems[0].customScoreCell(filteredItems[0].key)
                    : (filteredItems[0].scores || []).map((score) =>
                        scoreCheckbox(filteredItems[0].key, score),
                      )}
                </td>
              </>
            )}
          </tr>
          {filteredItems.slice(1).map((item) =>
            item.customCheck ? (
              item.customCheck
            ) : (
              <tr key={item.key}>
                <td style={tdLeft}>{item.label}</td>
                {checkPair(item.key)}
                <td
                  style={{
                    ...tdStyle,
                    textAlign: item.customScoreCell ? "left" : "center",
                  }}
                >
                  {item.customScoreCell
                    ? item.customScoreCell(item.key)
                    : (item.scores || []).map((score) =>
                        scoreCheckbox(item.key, score),
                      )}
                </td>
              </tr>
            ),
          )}
        </>
      );
    };

    const jointKeysList = [
      { label: "Shoulder (어깨관절) Lt", key: "ShoulderLt" },
      { label: "Shoulder (어깨관절) Rt", key: "ShoulderRt" },
      { label: "Elbow (팔꿈치관절) Lt", key: "ElbowLt" },
      { label: "Elbow (팔꿈치관절) Rt", key: "ElbowRt" },
      { label: "Wrist (손목관절) Lt", key: "WristLt" },
      { label: "Wrist (손목관절) Rt", key: "WristRt" },
      { label: "Hip (고관절) Lt", key: "HipLt" },
      { label: "Hip (고관절) Rt", key: "HipRt" },
      { label: "Knee (무릎관절) Lt", key: "KneeLt" },
      { label: "Knee (무릎관절) Rt", key: "KneeRt" },
      { label: "Ankle (발목관절) Lt", key: "AnkleLt" },
      { label: "Ankle (발목관절) Rt", key: "AnkleRt" },
    ].map((obj) => ({
      ...obj,
      customScoreCell: renderJointScoreCell,
    }));

    const reflexKeysList = [
      { label: "Biceps Reflex (상완이두근) Lt", key: "BicepsLt" },
      { label: "Biceps Reflex (상완이두근) Rt", key: "BicepsRt" },
      { label: "Triceps Reflex (상완삼두근) Lt", key: "TricepsLt" },
      { label: "Triceps Reflex (상완삼두근) Rt", key: "TricepsRt" },
      { label: "Patellar Reflex (슬개건) Lt", key: "PatellarLt" },
      { label: "Patellar Reflex (슬개건) Rt", key: "PatellarRt" },
      { label: "Achilles Reflex (아킬레스건) Lt", key: "AchillesLt" },
      { label: "Achilles Reflex (아킬레스건) Rt", key: "AchillesRt" },
    ].map((obj) => ({
      ...obj,
      scores: ["0", "1+", "2+(정상)", "3+", "4+"],
    }));

    const simpleExamGroups = [
      renderGroup("1. 정신 상태", [
        {
          label: "Alertness",
          key: "Alertness",
          scores: ["0", "1", "2", "3", "4", "5"],
        },
        {
          label: "Orientation",
          key: "Orientation",
          scores: ["0", "1", "2", "3"],
        },
        { label: "Memory", key: "Memory", scores: ["정상", "저하", "소실"] },
        {
          label: "Speech",
          key: "Speech",
          scores: ["Normal", "Dysarthria", "Motor", "Sensory"],
        },
      ]),
      renderGroup(
        <>
          2. 뇌신경
          <br />
          기능
        </>,
        [
          {
            label: "Pupil size / Light reflex",
            key: "Pupil",
            scores: ["Isocoria", "Anisocoria"],
          },
          {
            label: "EOM / Nystagmus",
            key: "EOM",
            scores: ["정상", "제한", "Nystagmus(+)"],
          },
          {
            label: "Facial sensory / motor",
            key: "Facial",
            scores: ["정상", "감각저하", "마비"],
          },
          {
            label: "Gag reflex / Tongue deviation",
            key: "Gag",
            scores: ["정상", "편향(좌/우)", "감소"],
          },
        ],
      ),
      renderGroup("3. 운동 기능", [
        {
          label: "RUE 근력",
          key: "RUE",
          scores: ["0", "1", "2", "3", "4", "5"],
        },
        {
          label: "RLE 근력",
          key: "RLE",
          scores: ["0", "1", "2", "3", "4", "5"],
        },
        {
          label: "LUE 근력",
          key: "LUE",
          scores: ["0", "1", "2", "3", "4", "5"],
        },
        {
          label: "LLE 근력",
          key: "LLE",
          scores: ["0", "1", "2", "3", "4", "5"],
        },
        ...jointKeysList,
        {
          label: "Muscle tone(Up)",
          key: "ToneUp",
          scores: ["정상", "경직", "강직", "이완"],
        },
        {
          label: "Muscle tone(Lo)",
          key: "ToneLo",
          scores: ["정상", "경직", "강직", "이완"],
        },
      ]),
      renderGroup("4. 소뇌 기능", [
        {
          label: "Finger-to-nose",
          key: "Finger",
          scores: ["정상", "Tremor", "Error"],
        },
        {
          label: "Heel-to-shin",
          key: "Heel",
          scores: ["정상", "Tremor", "Miss"],
        },
        {
          label: "Romberg’s test",
          key: "Romberg",
          scores: ["정상", "흔들림", "넘어짐"],
        },
      ]),
      renderGroup("5. 보행 및 자세", [
        {
          label: "Walking",
          key: "Walking",
          scores: ["정상", "불안정", "불가능"],
        },
        {
          label: "Tandem gait",
          key: "Tandem",
          scores: ["정상", "불안정", "불가능"],
        },
      ]),
    ];
    const generalExamGroups = [
      renderGroup("1. 정신 상태", [
        {
          label: "Alertness",
          key: "Alertness",
          scores: ["0", "1", "2", "3", "4", "5"],
        },
        {
          label: "Orientation",
          key: "Orientation",
          scores: ["0", "1", "2", "3"],
        },
        { label: "Memory", key: "Memory", scores: ["정상", "저하", "소실"] },
        {
          label: "Speech",
          key: "Speech",
          scores: ["Normal", "Dysarthria", "Motor", "Sensory"],
        },
      ]),
      renderGroup(
        <>
          2. 뇌신경
          <br />
          기능
        </>,
        [
          {
            label: "Pupil size / Light reflex",
            key: "Pupil",
            scores: ["Isocoria", "Anisocoria"],
          },
          {
            label: "EOM / Nystagmus",
            key: "EOM",
            scores: ["정상", "제한", "Nystagmus(+)"],
          },
          {
            label: "Facial sensory / motor",
            key: "Facial",
            scores: ["정상", "감각저하", "마비"],
          },
          {
            label: "Gag reflex / Tongue deviation",
            key: "Gag",
            scores: ["정상", "편향(좌/우)", "감소"],
          },
        ],
      ),
      renderGroup("3. 운동 기능", [
        {
          label: "RUE 근력",
          key: "RUE",
          scores: ["0", "1", "2", "3", "4", "5"],
        },
        {
          label: "RLE 근력",
          key: "RLE",
          scores: ["0", "1", "2", "3", "4", "5"],
        },
        {
          label: "LUE 근력",
          key: "LUE",
          scores: ["0", "1", "2", "3", "4", "5"],
        },
        {
          label: "LLE 근력",
          key: "LLE",
          scores: ["0", "1", "2", "3", "4", "5"],
        },
        ...jointKeysList,
        {
          label: "Muscle tone(Up)",
          key: "ToneUp",
          scores: ["정상", "경직", "강직", "이완"],
        },
        {
          label: "Muscle tone(Lo)",
          key: "ToneLo",
          scores: ["정상", "경직", "강직", "이완"],
        },
      ]),
      renderGroup("4. 감각 기능", [
        { label: "RUE 감각", key: "SensRUE", scores: ["정상", "저하", "소실"] },
        { label: "RLE 감각", key: "SensRLE", scores: ["정상", "저하", "소실"] },
        { label: "LUE 감각", key: "SensLUE", scores: ["정상", "저하", "소실"] },
        { label: "LLE 감각", key: "SensLLE", scores: ["정상", "저하", "소실"] },
      ]),
      renderGroup("5. 반사", [
        {
          label: "DTR",
          key: "DTR",
          scores: ["0", "1+", "2+(정상)", "3+", "4+"],
        },
        ...reflexKeysList,
        {
          label: "Babinski sign",
          key: "Babinski",
          customCheck: (
            <tr key="Babinski">
              <td style={tdLeft}>Babinski sign</td>
              <td style={tdStyle}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                  }}
                  onClick={() => {
                    const isChecked = reportData["Babinski"]?.normal || false;
                    setReportData((prev) => ({
                      ...prev,
                      Babinski: { normal: !isChecked, abnormal: false },
                    }));
                  }}
                >
                  {renderCustomCheckbox(
                    reportData["Babinski"]?.normal || false,
                    () => {},
                    "blue",
                  )}
                  (-)
                </label>
              </td>
              <td style={{ ...tdStyle, textAlign: "left" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                  }}
                  onClick={() => {
                    const isChecked = reportData["Babinski"]?.abnormal || false;
                    setReportData((prev) => ({
                      ...prev,
                      Babinski: { normal: false, abnormal: !isChecked },
                    }));
                  }}
                >
                  {renderCustomCheckbox(
                    reportData["Babinski"]?.abnormal || false,
                    () => {},
                    "red",
                  )}
                  (+)
                </label>
              </td>
              <td style={tdStyle}></td>
            </tr>
          ),
        },
      ]),
      renderGroup("6. 소뇌 기능", [
        {
          label: "Finger-to-nose",
          key: "Finger",
          scores: ["정상", "Tremor", "Error"],
        },
        {
          label: "Heel-to-shin",
          key: "Heel",
          scores: ["정상", "Tremor", "Miss"],
        },
        {
          label: "Romberg’s test",
          key: "Romberg",
          scores: ["정상", "흔들림", "넘어짐"],
        },
      ]),
      renderGroup("7. 보행 및 자세", [
        {
          label: "Walking",
          key: "Walking",
          scores: ["정상", "불안정", "불가능"],
        },
        {
          label: "Tandem gait",
          key: "Tandem",
          scores: ["정상", "불안정", "불가능"],
        },
      ]),
    ];

    return (
      <>
        <div id="neuro-table">
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid #E2E8F0",
              borderTop: "none",
            }}
          >
            <thead>
              <tr>
                <th style={thStyle}>중분류</th>
                <th style={thStyle}>소분류 항목</th>
                <th style={thStyle}>정상</th>
                <th style={thStyle}>비정상</th>
                <th style={thStyle}>점수 / 등급</th>
              </tr>
            </thead>
            <tbody>
              {(activeExamType === "simple"
                ? simpleExamGroups
                : generalExamGroups
              ).map((group, idx) => (
                <React.Fragment key={idx}>{group}</React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const handleSearch = () => {
    setAppliedKeyword(searchKeyword.trim());
  };

  const handleRefresh = () => {
    if (currentMonthView) {
      fetchMonthData(currentMonthView, true);
    }
  };

  const enterDeleteMode = () => {
    setSelectedRowKeys([]);
    setDeleteMode(true);
  };

  const exitDeleteMode = () => {
    setSelectedRowKeys([]);
    setDeleteMode(false);
  };

  const handleDeleteTests = () => {
    if (selectedRowKeys.length === 0) {
      window.alert("삭제할 항목을 선택해주세요.");
      return;
    }
    Modal.confirm({
      icon: null,
      title: `선택한 검사 기록 ${selectedRowKeys.length}건을 삭제하시겠습니까?`,
      content: (
        <span style={{ wordBreak: "keep-all" }}>
          삭제된 검사 기록은 되돌릴 수 없습니다.
        </span>
      ),
      okText: "삭제",
      okButtonProps: { danger: true },
      cancelText: "취소",
      centered: true,
      onOk: async () => {
        const targets = patientList.filter((p) =>
          selectedRowKeys.includes(p.dateTime),
        );
        // deletePatientData 함수가 검사 문서와 Storage 사진을 함께 지우고 로그를 남긴다.
        const deletePatientData = httpsCallable(functions, "deletePatientData");
        const results = await Promise.allSettled(
          targets.map((r) =>
            deletePatientData({ patientId: r.patientId, createdAt: r.dateTime }),
          ),
        );

        // 성공한 행만 목록에서 제거한다. 실패한 행은 남겨서 재시도할 수 있게 한다.
        const deletedIds = new Set(
          targets
            .filter((_, i) => results[i].status === "fulfilled")
            .map((r) => r.dateTime),
        );
        setPatientList((prev) => prev.filter((p) => !deletedIds.has(p.dateTime)));
        setData((prev) =>
          prev.map((p) => ({
            ...p,
            tests: p.tests.filter((t) => !deletedIds.has(t.id)),
          })),
        );
        exitDeleteMode();

        const failed = results.filter((res) => res.status === "rejected");
        if (failed.length > 0) {
          console.error(
            "검사 기록 삭제 실패:",
            failed.map((f) => f.reason),
          );
          window.alert(
            `${failed.length}건 삭제에 실패했습니다. 다시 시도해주세요.`,
          );
        }
      },
    });
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: "6%",
      render: (text, record, index) => (
        <span style={{ color: "#94a3b8" }}>
          {String(index + 1).padStart(2, "0")}
        </span>
      ),
    },
    {
      title: "환자명",
      dataIndex: "name",
      key: "name",
      width: "15%",
      render: (text) => (
        <span style={{ fontWeight: "600", color: "#334155" }}>{text}</span>
      ),
    },
    {
      title: "번호",
      dataIndex: "patientId",
      key: "patientId",
      width: "20%",
      render: (text) => <span style={{ color: "#64748b" }}>{text}</span>,
    },
    {
      title: "검사 일자",
      dataIndex: "dateTime",
      key: "dateTime",
      width: "40%",
      render: (text) => {
        if (!text) return "";
        const parts = String(text).split("-");
        const d = parts[0];
        const t = parts[1];
        const dateStr =
          d.length === 8
            ? `${d.substring(0, 4)}.${d.substring(4, 6)}.${d.substring(6, 8)}`
            : d;
        const timeStr =
          t && t.length === 6
            ? `${t.substring(0, 2)}:${t.substring(2, 4)}:${t.substring(4, 6)}`
            : t;

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontWeight: "bold", color: "#1e293b" }}>
              {dateStr}
            </span>
            <span style={{ color: "#94a3b8", fontSize: "13px" }}>
              {timeStr}
            </span>
          </div>
        );
      },
    },
    {
      title: "",
      key: "status",
      width: "14%",
      align: "start",
      render: (_, record) => (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "4px 12px",
            borderRadius: "15px",
            backgroundColor: record.isChecked ? "#EFF6FF" : "#F1F5F9",
            color: record.isChecked ? "#2C6AFF" : "#818C9E",
            fontSize: "11px",
            fontWeight: "bold",
          }}
        >
          <div
            style={{
              width: "5px",
              height: "5px",
              borderRadius: "50%",
              backgroundColor: record.isChecked ? "#2C6AFF" : "#818C9E",
            }}
          />
          {record.isChecked ? "완료" : "미완료"}
        </div>
      ),
    },
  ];

  const exportToPDF = async () => {
    const element = document.getElementById("neuro-table");
    if (!element) return;
    const canvas = await html2canvas(element, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgProps = pdf.getImageProperties(imgData);
    const imgHeight = (imgProps.height * pageWidth) / imgProps.width;
    pdf.addImage(imgData, "PNG", 0, 0, pageWidth, imgHeight);
    pdf.save(`${patientNo}-${selectedTest.id}-${patientName}.pdf`);
  };

  const handleCopy = async () => {
    if (!validateReportData()) return;
    try {
      const summaryText = `얼굴대칭: ${avgAvgRatio}% (상관: ${avgEyeRatio}%, 하관: ${avgMouthRatio}%) ${isFaceNonTest ? "Non Test" : isSmileText}
동공운동: ${avgPupilData}% (${pass_count}/${total_count}) 왼쪽 ${leftFailCount}회 실패, 오른쪽 ${rightFailCount}회 실패${isPupilNonTest ? " Non Test" : ""}
왼팔편측: GRADE ${isLeftMotorNonTest ? "-" : leftMotorGrade} (평균 각도: ${leftAngleAvgData.toFixed(1)}°)${isLeftMotorNonTest ? " Non Test" : ""}
오른팔편측: GRADE ${isRightMotorNonTest ? "-" : rightMotorGrade} (평균 각도: ${rightAngleAvgData.toFixed(1)}°)${isRightMotorNonTest ? " Non Test" : ""}
언어능력: ${avgSpeechData}% (정확도: ${speechAccuracy}%, 유창성: ${speechFluency}%, 완성도: ${speechCompleteness}%)${isSpeechNonTest ? " Non Test" : ""}`;

      const currentAllowedKeys = getVisibleKeys();
      const tableLines = [];
      const abnormalDescriptions = [];

      Object.keys(currentAllowedKeys).forEach((key) => {
        let score = reportData[key]?.score;
        let isAbnormal = reportData[key]?.abnormal;
        let isFullyChecked = true;

        let customTableLine = null;

        if (key === "Babinski") {
          if (reportData["Babinski"]?.normal) score = "(-)";
          if (reportData["Babinski"]?.abnormal) {
            score = "(+)";
            isAbnormal = true;
          }
        }

        if (isJointKey(key)) {
          if (reportData[key]?.normal) {
            score = "G0";
            customTableLine = `${tableLabels[key]} Tone: Normal (MAS Grade 0)`;
          } else if (reportData[key]?.abnormal) {
            const t = reportData[key]?.type || "";
            const s = reportData[key]?.score || "";

            if (!t || !s) {
              isFullyChecked = false;
            } else {
              const gradeNum = s.replace("G", "");
              customTableLine = `${tableLabels[key]} Tone: ${t} (MAS Grade ${gradeNum})`;
            }

            score = s;
            isAbnormal = true;
          }
        }

        if (score && isFullyChecked) {
          if (customTableLine) {
            tableLines.push(customTableLine);
          } else {
            const displayLabel = tableLabels[key] || key;
            tableLines.push(`${displayLabel}: ${score}`);
          }
        }

        if (isAbnormal && score && isFullyChecked) {
          let desc = "";
          const s = String(score);
          const jointName = tableLabels[key] || key;

          if (isJointKey(key)) {
            const typeStr = reportData[key]?.type;
            const gradeStr = reportData[key]?.score || "";
            if (typeStr === "Spasticity") {
              desc = `해당 관절(${jointName}) 수동 기동 시 속도 의존적인 저항 증가(경직, Spasticity) 소견을 보이며, 수정 애쉬워스 척도(MAS) Grade ${gradeStr} 수준의 근긴장도 항진 상태를 나타냄`;
            } else if (typeStr === "Rigidity") {
              desc = `해당 관절(${jointName})의 전 가동 범위에 걸쳐 속도와 무관한 지속적이고 납관 같은 저항(강직, Rigidity)이 감지되어, 소뇌 또는 추체외로계 기능 이상 가능성을 시사함`;
            } else if (typeStr === "Hypotonia") {
              desc = `해당 관절(${jointName}) 주위 근육의 장력이 비정상적으로 감소하여 수동 운동 시 저항이 거의 없는 늘어짐(이완성 저하, Hypotonia) 소견이 확인됨`;
            } else {
              desc = `해당 관절(${jointName})의 비정상 소견이 관찰됨(Grade ${gradeStr})`;
            }
          } else {
            switch (key) {
              case "Alertness":
                if (["3", "4"].includes(s))
                  desc =
                    "졸음이 오는 상태(Drowsy)로 보이며, 자극 시 깨어났다가 다시 잠드는 양상 관찰됨";
                else if (["1", "2"].includes(s))
                  desc =
                    "강한 자극에만 반응하거나 의식 확인이 어려운 혼미(Stupor/Coma) 상태 관찰됨";
                break;
              case "Orientation":
                if (["1", "2"].includes(s))
                  desc =
                    "현재 위치나 시간 등에 대해 부분적인 혼돈을 보이는 지남력 저하 상태 관찰됨";
                break;
              case "Speech":
                if (s === "Dysarthria")
                  desc =
                    "언어 이해는 가능하나 발음이 어눌하고 명확하지 않은 구음장애 소견 관찰됨";
                else desc = `언어 기능 비정상(${s}) 소견 관찰됨`;
                break;
              case "Pupil":
                if (s === "Anisocoria")
                  desc =
                    "양측 동공의 크기가 비대칭이며 대광반사가 지연되거나 소실된 양상 관찰됨";
                break;
              case "EOM":
                desc = `안구 운동에 비정상(${s}) 소견 관찰됨`;
                break;
              case "Facial":
                if (s === "마비")
                  desc =
                    "안면 근육의 비대칭이 관찰되며, 특정 부위의 운동 기능 마비가 의심됨";
                else if (s === "감각저하")
                  desc = "안면부 감각 저하 소견 관찰됨";
                break;
              case "RUE":
              case "RLE":
              case "LUE":
              case "LLE":
                if (s === "4")
                  desc = `${jointName}에서 검사자의 강한 저항에 근력이 다소 저하된 양상 관찰됨`;
                else if (s === "3")
                  desc = `${jointName}에서 저항을 이기지 못하는 근력 저하 관찰됨`;
                else if (["1", "2"].includes(s))
                  desc = `${jointName}에서 심한 근력 저하 상태 관찰됨`;
                else if (s === "0")
                  desc = `${jointName}의 근수축이 전혀 관찰되지 않는 완전 마비 상태 관찰됨`;
                break;
              case "SensRUE":
              case "SensRLE":
              case "SensLUE":
              case "SensLLE":
                if (s === "저하" || s === "소실")
                  desc = `${jointName}의 감각 인지 능력이 무뎌진 저하 상태 관찰됨`;
                break;
              case "DTR":
              case "BicepsLt":
              case "BicepsRt":
              case "TricepsLt":
              case "TricepsRt":
              case "PatellarLt":
              case "PatellarRt":
              case "AchillesLt":
              case "AchillesRt":
                if (s === "0")
                  desc = `${jointName} 자극에 대한 반응이 전혀 나타나지 않는 소실(Absent, 0) 상태로 신경근 단절 손상 가능성이 의심됨`;
                else if (s === "1+")
                  desc = `${jointName} 자극 시 반사 반응이 유의미하게 둔화된 저하(Diminished, 1+) 소견 관찰됨`;
                else if (s === "3+")
                  desc = `${jointName} 자극에 대해 과도한 근수축 및 반사 항진(Hyperactive, 3+) 반응을 보임`;
                else if (s === "4+")
                  desc = `${jointName} 타격 시 극심한 반사 항진과 함께 진간대(Clonus, 4+) 소견이 동반됨`;
                break;
              case "Babinski":
                if (s === "(+)")
                  desc =
                    "바빈스키 반사가 양성으로 나타나 상위 운동 신경원 손상 가능성이 시사됨";
                break;
              case "Finger":
              case "Heel":
                if (["Tremor", "Error", "Miss"].includes(s))
                  desc = `${jointName} 목표 지점 도달 전 떨림이나 거리 조절 실패 관찰됨`;
                break;
              case "Romberg":
                if (["흔들림", "넘어짐"].includes(s))
                  desc =
                    "균형 유지 능력이 저하되어 신체가 흔들리는 양상 관찰됨";
                break;
              case "Walking":
              case "Tandem":
                if (["불안정", "불가능"].includes(s))
                  desc = "보행 시 중심을 잡기 어려운 불안정한 양상 관찰됨";
                break;
              default:
                desc = `${jointName} 비정상 소견 관찰됨`;
            }
          }
          if (desc) abnormalDescriptions.push(desc);
        }
      });

      const tableText = tableLines.join("\n");

      let comprehensiveOpinion = `\n\n[종합 소견]\n`;

      if (selectedDisease) {
        comprehensiveOpinion += `[상병코드 ${selectedDisease.code}] ${selectedDisease.desc}으로 내원한 환자로, `;
      } else {
        comprehensiveOpinion += `내원한 환자로, `;
      }

      if (abnormalDescriptions.length > 0) {
        comprehensiveOpinion += `신경학적 검사상 ${abnormalDescriptions.join(" 및 ")}. `;
      } else {
        comprehensiveOpinion += `신경학적 검사상 특이 소견 관찰되지 않음. `;
      }

      if (selectedDisease && selectedDisease.mri === "유") {
        comprehensiveOpinion += `신경학적 병변 배제하기 위해 건강보험 급여 기준으로 사전 뇌 MRI 촬영을 즉시 시행하기로 결정함.`;
      }

      const finalText = isReportAttached
        ? `${summaryText}\n\n${tableText}${comprehensiveOpinion}`.trim()
        : `${summaryText}\n\n${tableText}`.trim();

      setCopyText(finalText);
      setCopyModalOpen(true);
    } catch (error) {
      console.error("미리보기 텍스트 생성 실패:", error);
    }
  };
  const executeCopy = async () => {
    if (window.confirm("복사하시겠습니까?")) {
      try {
        await navigator.clipboard.writeText(copyText);
        setCopyToastVisible(true);
        setTimeout(() => setCopyToastVisible(false), 2500);
        setCopyModalOpen(false);
      } catch (error) {
        console.error("복사 실패:", error);
        alert("복사에 실패했습니다.");
      }
    }
  };
  const handleSaveReport = async () => {
    if (!validateReportData()) return;
    setIsSaving(true);
    try {
      const docRef = doc(
        db,
        "Accounts",
        user.id,
        "Patients",
        selectedTest.patientId,
        "Tests",
        selectedTest.id,
      );

      const updatedData = {
        reports: reportData,
        bpDiastolic: bpDiastolic,
        bpSystolic: bpSystolic,
        note: remarksText,
      };

      const annotationUpdates = {};
      if (imagesLoaded) {
        if (faceImage1) {
          annotationUpdates.expressionless = {
            markers: imageMarkers[faceImage1] || [],
            lines: imageLines[faceImage1] || [],
          };
        }
        if (faceImage2) {
          annotationUpdates.smile = {
            markers: imageMarkers[faceImage2] || [],
            lines: imageLines[faceImage2] || [],
          };
        }
        if (tongueImage) {
          annotationUpdates.tongue = {
            markers: imageMarkers[tongueImage] || [],
            lines: imageLines[tongueImage] || [],
          };
        }
      }

      const firestoreData = { ...updatedData };
      for (const [key, value] of Object.entries(annotationUpdates)) {
        firestoreData[`annotations.${key}`] = value;
      }

      await updateDoc(docRef, firestoreData);

      const applyUpdate = (target) => ({
        ...target,
        ...updatedData,
        annotations: { ...(target?.annotations || {}), ...annotationUpdates },
      });

      setSelectedTest((prev) => applyUpdate(prev));

      setData((prevData) =>
        prevData.map((dt) => {
          if (dt.id !== selectedTest.patientId) return dt;
          return {
            ...dt,
            tests: dt.tests.map((t) =>
              t.id === selectedTest.id ? applyUpdate(t) : t,
            ),
          };
        }),
      );
      setSaveToastVisible(true);
      setTimeout(() => setSaveToastVisible(false), 2500);
    } catch (error) {
      console.error("저장 오류:", error);
      alert("저장 실패");
    } finally {
      setIsSaving(false);
    }
  };
  const handleReportAttachToggle = () => {
    const newValue = !isReportAttached;
    setIsReportAttached(newValue);
    localStorage.setItem("isReportAttached", JSON.stringify(newValue));
  };
  const renderReportModalContent = () => {
    const keyLabels = {
      Alertness: "Alertness",
      Orientation: "Orientation",
      Memory: "Memory",
      Speech: "Speech",
      Pupil: "Pupil / Light reflex",
      EOM: "EOM / Nystagmus",
      Facial: "Facial sensory/motor",
      Gag: "Gag reflex / Tongue deviation",
      RUE: "RUE 근력",
      RLE: "RLE 근력",
      LUE: "LUE 근력",
      LLE: "LLE 근력",
      ShoulderLt: "Shoulder (어깨관절) Lt",
      ShoulderRt: "Shoulder (어깨관절) Rt",
      ElbowLt: "Elbow (팔꿈치관절) Lt",
      ElbowRt: "Elbow (팔꿈치관절) Rt",
      WristLt: "Wrist (손목관절) Lt",
      WristRt: "Wrist (손목관절) Rt",
      HipLt: "Hip (고관절) Lt",
      HipRt: "Hip (고관절) Rt",
      KneeLt: "Knee (무릎관절) Lt",
      KneeRt: "Knee (무릎관절) Rt",
      AnkleLt: "Ankle (발목관절) Lt",
      AnkleRt: "Ankle (발목관절) Rt",
      SensRUE: "RUE 감각",
      SensRLE: "RLE 감각",
      SensLUE: "LUE 감각",
      SensLLE: "LLE 감각",
      DTR: "DTR",
      BicepsLt: "Biceps Reflex (상완이두근) Lt",
      BicepsRt: "Biceps Reflex (상완이두근) Rt",
      TricepsLt: "Triceps Reflex (상완삼두근) Lt",
      TricepsRt: "Triceps Reflex (상완삼두근) Rt",
      PatellarLt: "Patellar Reflex (슬개건) Lt",
      PatellarRt: "Patellar Reflex (슬개건) Rt",
      AchillesLt: "Achilles Reflex (아킬레스건) Lt",
      AchillesRt: "Achilles Reflex (아킬레스건) Rt",
      Babinski: "Babinski sign",
      Finger: "Finger-to-nose",
      Heel: "Heel-to-shin",
      Romberg: "Romberg's test",
      Walking: "Walking",
      Tandem: "Tandem gait",
    };

    const simpleGroups = [
      {
        id: "01",
        title: "정신 상태",
        keys: ["Alertness", "Orientation", "Memory", "Speech"],
      },
      {
        id: "02",
        title: "뇌신경 기능",
        keys: ["Pupil", "EOM", "Facial", "Gag"],
      },
      {
        id: "03",
        title: "운동 기능",
        keys: [
          "RUE",
          "RLE",
          "LUE",
          "LLE",
          "ShoulderLt",
          "ShoulderRt",
          "ElbowLt",
          "ElbowRt",
          "WristLt",
          "WristRt",
          "HipLt",
          "HipRt",
          "KneeLt",
          "KneeRt",
          "AnkleLt",
          "AnkleRt",
        ],
      },
      { id: "04", title: "소뇌 기능", keys: ["Finger", "Heel", "Romberg"] },
      { id: "05", title: "보행 및 자세", keys: ["Walking", "Tandem"] },
    ];

    const generalGroups = [
      {
        id: "01",
        title: "정신 상태",
        keys: ["Alertness", "Orientation", "Memory", "Speech"],
      },
      {
        id: "02",
        title: "뇌신경 기능",
        keys: ["Pupil", "EOM", "Facial", "Gag"],
      },
      {
        id: "03",
        title: "운동 기능",
        keys: [
          "RUE",
          "RLE",
          "LUE",
          "LLE",
          "ShoulderLt",
          "ShoulderRt",
          "ElbowLt",
          "ElbowRt",
          "WristLt",
          "WristRt",
          "HipLt",
          "HipRt",
          "KneeLt",
          "KneeRt",
          "AnkleLt",
          "AnkleRt",
        ],
      },
      {
        id: "04",
        title: "감각 기능",
        keys: ["SensRUE", "SensRLE", "SensLUE", "SensLLE"],
      },
      {
        id: "05",
        title: "반사",
        keys: [
          "DTR",
          "BicepsLt",
          "BicepsRt",
          "TricepsLt",
          "TricepsRt",
          "PatellarLt",
          "PatellarRt",
          "AchillesLt",
          "AchillesRt",
          "Babinski",
        ],
      },
      { id: "06", title: "소뇌 기능", keys: ["Finger", "Heel", "Romberg"] },
      { id: "07", title: "보행 및 자세", keys: ["Walking", "Tandem"] },
    ];

    const reportGroups =
      activeExamType === "simple" ? simpleGroups : generalGroups;
    const currentAllowedKeys = getVisibleKeys();

    return reportGroups.map((group) => {
      const validItems = group.keys
        .map((key) => {
          if (!(key in currentAllowedKeys)) return null;

          let score = reportData[key]?.score;
          if (key === "Babinski") {
            if (reportData["Babinski"]?.normal) score = "(-)";
            if (reportData["Babinski"]?.abnormal) score = "(+)";
          }
          if (!score) return null;

          let desc = `${score} 소견을 보임.`;
          const s = String(score);

          if (isJointKey(key)) {
            const jointName = keyLabels[key];
            if (reportData[key]?.normal) {
              desc = `해당 관절 부위(${jointName})의 수동 운동 시 관절 장력 및 이완 저항이 정상적인 범위를 유지하고 있으며, 비정상적인 경직이나 강직 소견이 관찰되지 않음.`;
            } else {
              const typeStr = reportData[key]?.type;
              if (typeStr === "Spasticity") {
                desc = `해당 관절(${jointName}) 수동 기동 시 속도 의존적인 저항 증가(경직, Spasticity) 소견을 보이며, 수정 애쉬워스 척도(MAS) Grade ${s} 수준의 근긴장도 항진 상태를 나타냄`;
              } else if (typeStr === "Rigidity") {
                desc = `해당 관절(${jointName})의 전 가동 범위에 걸쳐 속도와 무관한 지속적이고 납관 같은 저항(강직, Rigidity)이 감지되어, 소뇌 또는 추체외로계 기능 이상 가능성을 시사함.`;
              } else if (typeStr === "Hypotonia") {
                desc = `해당 관절(${jointName}) 주위 근육의 장력이 비정상적으로 감소하여 수동 운동 시 저항이 거의 없는 늘어짐(이완성 저하, Hypotonia) 소견이 확인됨.`;
              } else {
                desc = `해당 관절(${jointName})의 비정상 소견이 관찰됨 (Grade ${s}).`;
              }
            }
          } else {
            switch (key) {
              case "Alertness":
                if (s === "5" || s === "5점")
                  desc =
                    "의식 수준이 명료(Alert)하며, 외부의 자극에 대해 즉각적이고 적절한 반응을 보임.";
                else if (["3", "4"].includes(s))
                  desc =
                    "졸음이 오는 상태(Drowsy)로 보이며, 자극 시 깨어났다가 다시 잠드는 양상을 보임.";
                else if (["1", "2"].includes(s))
                  desc =
                    "강한 자극에만 반응하거나 의식 확인이 어려운 혼미(Stupor/Coma) 상태임.";
                break;
              case "Orientation":
                if (s === "3" || s === "3점")
                  desc =
                    "시간, 장소, 사람에 대한 지남력이 모두 정상적으로 유지됨.";
                else if (["1", "2"].includes(s))
                  desc =
                    "현재 위치나 시간 등에 대해 부분적인 혼돈을 보이는 지남력 저하 상태임.";
                break;
              case "Speech":
                if (s === "Normal")
                  desc = "언어 이해 및 표현 능력이 정상이며 발음이 명확함.";
                else if (s === "Dysarthria")
                  desc =
                    "언어 이해는 가능하나 발음이 어눌하고 명확하지 않은 구음장애 소견을 보임.";
                break;
              case "Pupil":
                if (s === "Isocoria")
                  desc =
                    "양측 동공 크기가 동일하며, 빛 자극에 대해 대광반사가 신속하고 정상적임.";
                else if (s === "Anisocoria")
                  desc =
                    "양측 동공의 크기가 비대칭이며 대광반사가 지연되거나 소실된 양상을 보임.";
                break;
              case "Facial":
                if (s === "정상")
                  desc =
                    "안면부 감각이 정상이며, 표정 근육의 움직임이 대칭적으로 잘 유지됨.";
                else if (s === "마비")
                  desc =
                    "안면 근육의 비대칭이 관찰되며, 특정 부위의 운동 기능 마비가 의심됨.";
                break;
              case "RUE":
              case "RLE":
              case "LUE":
              case "LLE":
                if (s === "5")
                  desc = "중력 및 강한 저항에 대해 정상적인 근력을 유지함.";
                else if (s === "4")
                  desc =
                    "중력에는 대항하나 검사자의 강한 저항에는 근력이 다소 저하된 양상을 보임.";
                else if (s === "3")
                  desc =
                    "중력에는 대항하여 움직일 수 있으나 저항을 이기지는 못함.";
                else if (["1", "2"].includes(s))
                  desc =
                    "관절의 움직임이 미미하거나 근육의 수축만 관찰되는 심한 근력 저하 상태임.";
                break;
              case "SensRUE":
              case "SensRLE":
              case "SensLUE":
              case "SensLLE":
                if (s === "정상")
                  desc =
                    "촉각, 통각 등 모든 부위의 감각 인지가 대칭적이고 정상적임.";
                else if (s === "저하")
                  desc =
                    "반대측 또는 정상 범위에 비해 감각 인지 능력이 무뎌진 저하 상태를 보임.";
                break;
              case "DTR":
              case "BicepsLt":
              case "BicepsRt":
              case "TricepsLt":
              case "TricepsRt":
              case "PatellarLt":
              case "PatellarRt":
              case "AchillesLt":
              case "AchillesRt":
                if (s === "2+(정상)")
                  desc =
                    "고무망치 자극 시 표준적이고 적절한 수축력의 고유수용성 건반사 반응(정상, 2+)을 보이며, 신경 전도 경로의 연속성이 안정적임.";
                else if (s === "0")
                  desc =
                    "자극에 대한 반응이 전혀 나타나지 않는 소실(Absent, 0) 상태로, 말초신경계(하부 운동신경원, LMN) 또는 신경근 단절 손상 가능성이 의심됨.";
                else if (s === "1+")
                  desc =
                    "자극 시 반사 반응이 평균치보다 유의미하게 둔화된 저하(Diminished, 1+) 소견을 보임.";
                else if (s === "3+")
                  desc =
                    "자극에 대해 정상 범위를 초과하는 과도한 근수축 및 반사 항진(Hyperactive, 3+) 반응을 보여 중추신경계 자극 유도를 시사함.";
                else if (s === "4+")
                  desc =
                    "타격 시 극심한 반사 항진과 함께 연속적인 굴곡 떨림인 진간대(Clonus, 4+) 소견이 동반되어, 상부 운동신경원(UMN) 병변 및 중추성 척수로 손상 위험성이 강력히 의심됨.";
                break;
              case "Babinski":
                if (s === "(-)")
                  desc = "바빈스키 반사가 음성으로, 정상적인 반응을 보임.";
                else if (s === "(+)")
                  desc =
                    "바빈스키 반사가 양성으로 나타나 상위 운동 신경원 손상 가능성이 시사됨.";
                break;
              case "Finger":
              case "Heel":
                if (s === "정상")
                  desc =
                    "동작이 정확하고 매끄러우며 겨냥 실패나 떨림이 관찰되지 않음.";
                else if (["Tremor", "Error", "Miss"].includes(s))
                  desc =
                    "목표 지점에 도달하기 전 떨림(Intentional Tremor)이나 거리 조절 실패가 관찰됨.";
                break;
              case "Romberg":
                if (s === "정상")
                  desc =
                    "눈을 감은 상태에서도 신체의 균형을 안정적으로 유지함.";
                else if (["흔들림", "넘어짐"].includes(s))
                  desc =
                    "균형 유지 능력이 저하되어 신체가 한쪽으로 기울거나 흔들리는 양상을 보임.";
                break;
              case "Walking":
              case "Tandem":
                if (s === "정상")
                  desc =
                    "보행 속도와 폭이 일정하며 팔의 움직임과 신체 균형이 조화로움.";
                else if (["불안정", "불가능"].includes(s))
                  desc =
                    "보행 시 보폭이 불규칙하거나 중심을 잡기 어려운 불안정한 양상을 보임.";
                break;
            }
          }

          return { key, label: keyLabels[key], score: s, desc };
        })
        .filter((item) => item !== null);

      if (validItems.length === 0) return null;

      return (
        <div
          key={group.id}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            marginBottom: "32px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              style={{ fontSize: "11px", color: "#818C9E", fontWeight: "600" }}
            >
              {group.id}
            </span>
            <span
              style={{ fontSize: "12px", color: "#364153", fontWeight: "bold" }}
            >
              {group.title}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              paddingLeft: "8px",
            }}
          >
            {validItems.map((item) => (
              <div
                key={item.key}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "16px",
                }}
              >
                <div
                  style={{
                    width: "140px",
                    flexShrink: 0,
                    fontSize: "11px",
                    color: "#818C9E",
                    paddingTop: "4px",
                  }}
                >
                  {item.label}
                </div>
                <div style={{ width: "60px", flexShrink: 0 }}>
                  <span
                    style={{
                      display: "inline-block",
                      backgroundColor: "#EFF6FF",
                      color: "#2C6AFF",
                      fontSize: "9px",
                      fontWeight: "bold",
                      padding: "4px 8px",
                      borderRadius: "12px",
                    }}
                  >
                    {item.score}
                  </span>
                </div>
                <div
                  style={{
                    flex: 1,
                    fontSize: "11px",
                    color: "#364153",
                    lineHeight: "1.5",
                    paddingTop: "2px",
                  }}
                >
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    });
  };
  const handleCheckAllNormal = () => {
    if (isAllChecked) {
      setReportData({});
      setIsAllChecked(false);
    } else {
      setIsAllChecked(true);
      const currentAllowedKeys = getVisibleKeys();
      setReportData((prevData) => {
        const newData = { ...prevData };
        Object.keys(currentAllowedKeys).forEach((key) => {
          if (checkAllBehavior === "fill_empty") {
            const existing = newData[key];
            if (
              existing &&
              (existing.normal || existing.abnormal || existing.score)
            ) {
              return;
            }
          }
          newData[key] = {
            normal: true,
            abnormal: false,
            score: isJointKey(key)
              ? "G0"
              : isReflexKey(key) || key === "DTR"
                ? "2+(정상)"
                : currentAllowedKeys[key],
            type: null,
          };
        });
        return newData;
      });
    }
  };
  const handleDateChange = (date) => {
    const selectedDate = date.format("YYYYMMDD");
    const newMonth = date.format("YYYYMM");

    setSelectedDateView(selectedDate);
    setCurrentMonthView(newMonth);
    setSelectedTest(undefined);

    if (calendarMode === "year") {
      setFilterMode("month");
    } else {
      setFilterMode("date");
    }

    fetchMonthData(newMonth);
  };

  const handlePanelChange = (date, mode) => {
    const newMonth = date.format("YYYYMM");
    setCurrentMonthView(newMonth);
    setCalendarMode(mode);

    if (mode === "year") {
      setFilterMode("month");
    } else {
      setFilterMode("date");
    }

    fetchMonthData(newMonth);
  };

  const dateCellRender = (value) => {
    const dateStr = value.format("YYYYMMDD");
    const hasData = patientList.some((item) => item.date === dateStr);
    if (!hasData) return null;
    return (
      <div
        style={{
          width: "4px",
          height: "4px",
          backgroundColor: "#2C6AFF",
          borderRadius: "50%",
          margin: "2px auto 0",
        }}
      />
    );
  };
  const calendarHeaderRender = ({ value, type, onChange, onTypeChange }) => {
    const year = value.year();
    const month = value.month();

    const yearOptions = [];
    for (let i = year - 5; i < year + 5; i += 1) {
      yearOptions.push({ label: `${i}년`, value: i });
    }
    const monthOptions = [];
    for (let i = 0; i < 12; i++) {
      monthOptions.push({ label: `${i + 1}월`, value: i });
    }

    return (
      <div style={{ padding: "8px 8px 16px 8px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <Select
              size="small"
              dropdownMatchSelectWidth={false}
              popupMatchSelectWidth={false}
              value={year}
              onChange={(newYear) => onChange(value.clone().year(newYear))}
              options={yearOptions}
              bordered={false}
              className="cal-custom-select"
              suffixIcon={<img src={calendarDownImg} crossOrigin="anonymous" />}
            />
            <Select
              size="small"
              dropdownMatchSelectWidth={false}
              popupMatchSelectWidth={false}
              value={month}
              onChange={(newMonth) => onChange(value.clone().month(newMonth))}
              options={monthOptions}
              bordered={false}
              className="cal-custom-select"
              suffixIcon={<img src={calendarDownImg} crossOrigin="anonymous" />}
            />
          </div>
          <div
            style={{
              display: "flex",
              gap: "4px",
              alignItems: "center",
              paddingRight: "8px",
            }}
          >
            <img
              className="cal-nav-btn"
              src={expandLeftImg}
              onClick={() => onChange(value.clone().subtract(1, "month"))}
            />
            <img
              className="cal-nav-btn"
              src={expandRightImg}
              onClick={() => onChange(value.clone().add(1, "month"))}
            />
          </div>
        </div>

        <Radio.Group
          value={type}
          onChange={(e) => onTypeChange(e.target.value)}
          className="custom-calendar-radio"
        >
          <Radio.Button value="month">월</Radio.Button>
          <Radio.Button value="year">년</Radio.Button>
        </Radio.Group>
      </div>
    );
  };
  const handleSaveAnnotations = async () => {
    const imageKey = getImageKey(previewImage);
    if (!imageKey) return;
    try {
      const testRef = doc(
        db,
        "Accounts",
        user.id,
        "Patients",
        selectedTest.patientId,
        "Tests",
        selectedTest.id,
      );
      const currentMarkers = imageMarkers[previewImage] || [];
      const currentLines = imageLines[previewImage] || [];
      await updateDoc(testRef, {
        [`annotations.${imageKey}`]: {
          markers: currentMarkers,
          lines: currentLines,
        },
      });
      setSelectedTest((prev) => ({
        ...prev,
        annotations: {
          ...prev.annotations,
          [imageKey]: { markers: currentMarkers, lines: currentLines },
        },
      }));
      alert("그리기 데이터가 저장되었습니다!");
    } catch (error) {
      alert("저장에 실패했습니다.");
    }
  };

  const getImageKey = (url) => {
    if (url === faceImage1) return "expressionless";
    if (url === faceImage2) return "smile";
    if (url === tongueImage) return "tongue";
    return null;
  };
  const openImagePreview = (imageUrl) => {
    if (!imageUrl) return;
    setPreviewImage(imageUrl);
    setPreviewScale(1.5);
    setDrawMode(null);
    setHistory([]);
    setRedoStack([]);
    setPreviewOpen(true);
  };

  const closeImagePreview = () => {
    setPreviewOpen(false);
    setPreviewImage("");
    setPreviewScale(1.5);
  };

  const zoomInPreview = () =>
    setPreviewScale((prev) => Math.min(prev + 0.25, 5));
  const zoomOutPreview = () =>
    setPreviewScale((prev) => Math.max(prev - 0.25, 0.5));
  const resetPreviewZoom = () => setPreviewScale(1.5);

  const handleImageMouseDown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const baseW = rect.width / previewScale;
    const baseH = rect.height / previewScale;
    const x = (e.clientX - rect.left) / previewScale;
    const y = (e.clientY - rect.top) / previewScale;

    if (drawMode === "check") {
      saveHistory();
      const newMarker = { id: Date.now(), x, y, baseW, baseH };
      setImageMarkers((prev) => ({
        ...prev,
        [previewImage]: [...(prev[previewImage] || []), newMarker],
      }));
    } else if (drawMode === "line") {
      setDrawingLine({ startX: x, startY: y, endX: x, endY: y, baseW, baseH });
    }
  };

  const handleImageMouseMove = (e) => {
    if (drawMode !== "line" || !drawingLine) return;
    const rect = e.currentTarget.getBoundingClientRect();
    let endX = (e.clientX - rect.left) / previewScale;
    let endY = (e.clientY - rect.top) / previewScale;
    if (e.shiftKey) endY = drawingLine.startY;
    setDrawingLine((prev) => ({ ...prev, endX, endY }));
  };

  const handleImageMouseUp = () => {
    if (drawMode === "line" && drawingLine) {
      const dx = drawingLine.endX - drawingLine.startX;
      const dy = drawingLine.endY - drawingLine.startY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance > 5) {
        saveHistory();
        const newLine = { id: Date.now(), ...drawingLine };
        setImageLines((prev) => ({
          ...prev,
          [previewImage]: [...(prev[previewImage] || []), newLine],
        }));
      }
      setDrawingLine(null);
    }
  };

  const handleMarkerClick = (e, markerId) => {
    e.stopPropagation();
    saveHistory();
    setImageMarkers((prev) => ({
      ...prev,
      [previewImage]: (prev[previewImage] || []).filter(
        (m) => m.id !== markerId,
      ),
    }));
  };

  const handleLineClick = (e, lineId) => {
    e.stopPropagation();
    saveHistory();
    setImageLines((prev) => ({
      ...prev,
      [previewImage]: (prev[previewImage] || []).filter((l) => l.id !== lineId),
    }));
  };
  const runDataMigration = async () => {
    const confirm = window.confirm(
      "모든 환자의 검사 기록에 hospitalId와 patientId를 추가합니다. 실행하시겠습니까?",
    );
    if (!confirm) return;
    try {
      let updatedCount = 0;
      const patientsSnap = await getDocs(
        collection(db, "Accounts", user.id, "Patients"),
      );
      for (const pDoc of patientsSnap.docs) {
        const patientId = pDoc.id;
        const testsSnap = await getDocs(
          collection(db, "Accounts", user.id, "Patients", patientId, "Tests"),
        );
        for (const tDoc of testsSnap.docs) {
          const testRef = doc(
            db,
            "Accounts",
            user.id,
            "Patients",
            patientId,
            "Tests",
            tDoc.id,
          );
          await updateDoc(testRef, {
            hospitalId: user.id,
            patientId: patientId,
          });
          updatedCount++;
        }
      }
      alert(
        `마이그레이션 완료! 총 ${updatedCount}개의 검사 기록이 업데이트되었습니다.`,
      );
    } catch (error) {
      alert("오류가 발생했습니다. 콘솔을 확인해주세요.");
    }
  };

  const exportLeftViewToPdf = async () => {
    if (!validateReportData()) return;
    setIsExporting(true);
    let restoreScreen = () => {};
    try {
      const leftPanel = document.getElementById("pdf-left-panel");
      const rightPanel = document.getElementById("pdf-right-panel");
      const reportPanel = document.getElementById("pdf-report-panel");

      const hideTop = document.getElementById("pdf-hide-top");
      const hideBottom = document.getElementById("pdf-hide-bottom");
      const legalText = document.getElementById("pdf-legal-text");
      const tableTitle = document.getElementById("pdf-table-title");

      if (!leftPanel || !rightPanel) {
        setIsExporting(false);
        return;
      }

      const currentScrollY = window.scrollY;
      const isAttachChecked = isReportAttached;

      const originalTopDisplay = hideTop ? hideTop.style.display : "";
      const originalBottomDisplay = hideBottom ? hideBottom.style.display : "";
      const originalLeftAlignSelf = leftPanel.style.alignSelf;
      const originalRightAlignSelf = rightPanel.style.alignSelf;
      const originalLeftCSS = {
        height: leftPanel.style.height,
        overflow: leftPanel.style.overflow,
        maxHeight: leftPanel.style.maxHeight,
      };
      const originalRightCSS = {
        height: rightPanel.style.height,
        overflow: rightPanel.style.overflow,
        maxHeight: rightPanel.style.maxHeight,
      };
      const swappedTextareas = [];

      restoreScreen = () => {
        swappedTextareas.forEach(({ ta, div }) => {
          if (div.parentNode) div.parentNode.removeChild(div);
          ta.style.display = "";
        });

        leftPanel.style.height = originalLeftCSS.height;
        leftPanel.style.overflow = originalLeftCSS.overflow;
        leftPanel.style.maxHeight = originalLeftCSS.maxHeight;
        leftPanel.style.alignSelf = originalLeftAlignSelf;
        rightPanel.style.height = originalRightCSS.height;
        rightPanel.style.overflow = originalRightCSS.overflow;
        rightPanel.style.maxHeight = originalRightCSS.maxHeight;
        rightPanel.style.alignSelf = originalRightAlignSelf;

        if (hideTop) hideTop.style.display = originalTopDisplay;
        if (hideBottom) hideBottom.style.display = originalBottomDisplay;
        if (legalText) legalText.style.display = "none";
        if (tableTitle) tableTitle.style.display = "none";

        if (reportPanel) {
          reportPanel.style.display = "none";
          reportPanel.style.height = "auto";
        }

        window.scrollTo(0, currentScrollY);
      };

      window.scrollTo(0, 0);

      if (isAttachChecked && reportPanel) {
        reportPanel.style.display = "block";
      }

      if (hideTop) hideTop.style.display = "none";
      if (hideBottom) hideBottom.style.display = "none";
      if (legalText) legalText.style.display = "block";
      if (tableTitle) tableTitle.style.display = "block";

      leftPanel.style.alignSelf = "flex-start";
      rightPanel.style.alignSelf = "flex-start";

      // html2canvas는 textarea 내부의 줄바꿈된 텍스트를 제대로 렌더하지 못해
      // 내용이 잘린다. 캡처 동안 textarea를 동일한 스타일의 div로 임시 대체한다.
      leftPanel.querySelectorAll("textarea").forEach((ta) => {
        const cs = window.getComputedStyle(ta);
        const div = document.createElement("div");
        div.textContent = ta.value;
        div.style.fontSize = cs.fontSize;
        div.style.fontFamily = cs.fontFamily;
        div.style.fontWeight = cs.fontWeight;
        div.style.fontStyle = cs.fontStyle;
        div.style.lineHeight = cs.lineHeight;
        div.style.color = cs.color;
        div.style.letterSpacing = cs.letterSpacing;
        div.style.textAlign = cs.textAlign === "start" ? "left" : cs.textAlign;
        div.style.padding = cs.padding;
        div.style.margin = "0";
        div.style.border = "none";
        div.style.background = "transparent";
        div.style.width = "100%";
        div.style.boxSizing = "border-box";
        div.style.whiteSpace = "pre-wrap";
        div.style.wordBreak = "break-word";
        div.style.minHeight = cs.height;
        ta.style.display = "none";
        ta.parentNode.insertBefore(div, ta.nextSibling);
        swappedTextareas.push({ ta, div });
      });

      await new Promise((resolve) => setTimeout(resolve, 300));

      const leftScrollHeight = leftPanel.scrollHeight;
      const rightScrollHeight = rightPanel.scrollHeight;
      const reportScrollHeight =
        isAttachChecked && reportPanel ? reportPanel.scrollHeight : 0;

      leftPanel.style.height = `${leftScrollHeight}px`;
      leftPanel.style.overflow = "visible";
      leftPanel.style.maxHeight = "none";
      rightPanel.style.height = `${rightScrollHeight}px`;
      rightPanel.style.overflow = "visible";
      rightPanel.style.maxHeight = "none";

      if (isAttachChecked && reportPanel) {
        reportPanel.style.height = `${reportScrollHeight}px`;
        reportPanel.style.overflow = "visible";
        reportPanel.style.maxHeight = "none";
      }

      await new Promise((resolve) => setTimeout(resolve, 300));

      const pdf = new jsPDF("p", "mm", "a4");

      const addImageMultiPage = (
        canvas,
        pdfInstance,
        centerVertically = false,
      ) => {
        if (canvas.width === 0 || canvas.height === 0) return;

        const imgData = canvas.toDataURL("image/png");
        const pdfW = pdfInstance.internal.pageSize.getWidth();
        const pdfH = pdfInstance.internal.pageSize.getHeight();

        const marginX = 10;
        const topMargin = 10;

        const imgW = pdfW - marginX * 2;
        const imgH = (canvas.height * imgW) / canvas.width;

        let position = topMargin;

        if (centerVertically && imgH < pdfH) {
          position = (pdfH - imgH) / 2;
        }


        let heightLeft = imgH + position;

        pdfInstance.addImage(imgData, "PNG", marginX, position, imgW, imgH);
        heightLeft -= pdfH;

        while (heightLeft > 0) {
          position -= pdfH;
          pdfInstance.addPage();
          pdfInstance.addImage(imgData, "PNG", marginX, position, imgW, imgH);
          heightLeft -= pdfH;
        }
      };


      const leftSection1 = document.getElementById("pdf-left-section-1");
      const leftSection2 = document.getElementById("pdf-left-section-2");
      const splitLeft = extendedActive && leftSection1 && leftSection2;

      if (splitLeft) {
        for (let i = 0; i < 2; i++) {
          const section = i === 0 ? leftSection1 : leftSection2;
          const sectionHeight = section.scrollHeight;
          if (i > 0) pdf.addPage();
          const canvasSection = await html2canvas(section, {
            scale: 2,
            useCORS: true,
            backgroundColor: "#ffffff",
            height: sectionHeight,
            windowHeight: sectionHeight,
            scrollY: 0,
          });
          if (canvasSection.width > 0 && canvasSection.height > 0) {
            addImageMultiPage(canvasSection, pdf, false);
          }
        }
      } else {
        const canvasLeft = await html2canvas(leftPanel, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          height: leftScrollHeight,
          windowHeight: leftScrollHeight,
          scrollY: 0,
        });
        if (canvasLeft.width > 0 && canvasLeft.height > 0) {
          addImageMultiPage(canvasLeft, pdf, false);
        }
      }

      pdf.addPage();
      const canvasRight = await html2canvas(rightPanel, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        height: rightScrollHeight,
        windowHeight: rightScrollHeight,
        scrollY: 0,
      });
      if (canvasRight.width > 0 && canvasRight.height > 0) {
        addImageMultiPage(canvasRight, pdf, true);
      }

      if (isAttachChecked && reportPanel) {
        pdf.addPage();
        const canvasReport = await html2canvas(reportPanel, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          height: reportScrollHeight,
          windowHeight: reportScrollHeight,
          scrollY: 0,
        });
        if (canvasReport.width > 0 && canvasReport.height > 0) {
          addImageMultiPage(canvasReport, pdf, false);
        }
      }

      pdf.save(`${patientNo}-${selectedTest.id}-${patientName}.pdf`);
    } catch (error) {
      console.error("PDF 내보내기 실패:", error);
      alert("PDF 내보내기 중 오류가 발생했습니다.");
    } finally {
      restoreScreen();
      setIsExporting(false);
    }
  };

  const calculateLineData = (x1, y1, x2, y2) => {
    const pxDistance = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    const cmDistance = (pxDistance * 0.026458).toFixed(1);
    let angle = Math.atan2(y1 - y2, x2 - x1) * (180 / Math.PI);
    if (angle < 0) angle += 180;
    if (angle === 180) angle = 0;
    if (angle > 90) angle = 180 - angle;
    return { cm: cmDistance, degree: angle.toFixed(1) };
  };

  const renderThumbnailAnnotations = (imageUrl) => {
    const markers = imageMarkers[imageUrl] || [];
    const lines = imageLines[imageUrl] || [];
    if (markers.length === 0 && lines.length === 0) return null;
    const firstItem = markers[0] || lines[0];
    const { baseW, baseH } = firstItem;
    if (!baseW || !baseH) return null;

    return (
      <svg
        viewBox={`0 0 ${baseW} ${baseH}`}
        preserveAspectRatio="xMidYMid meet"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: 5,
        }}
      >
        {lines.map((line) => {
          const { cm, degree } = calculateLineData(
            line.startX,
            line.startY,
            line.endX,
            line.endY,
          );

          const dx = line.endX - line.startX;
          const dy = line.endY - line.startY;
          const length = Math.sqrt(dx * dx + dy * dy);
          const ux = length > 0 ? dx / length : 0;
          const uy = length > 0 ? dy / length : 0;

          const offsetDegree = 15;
          const offsetCm = 25;

          const label1X = line.startX - ux * offsetDegree;
          const label1Y = line.startY - uy * offsetDegree;
          const label2X = line.endX + ux * offsetCm;
          const label2Y = line.endY + uy * offsetCm;

          return (
            <g key={`thumb-line-${line.id}`}>
              <line
                x1={line.startX}
                y1={line.startY}
                x2={line.endX}
                y2={line.endY}
                stroke="#3B82F6"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <text
                x={label1X}
                y={label1Y}
                textAnchor="middle"
                fill="#818C9E"
                fontSize="10"
                fontWeight="bold"
                style={{
                  textShadow: "1px 1px 1px white, -1px -1px 1px white",
                }}
              >
                {degree}°
              </text>
              <text
                x={label2X}
                y={label2Y}
                textAnchor="middle"
                fill="#818C9E"
                fontSize="10"
                fontWeight="bold"
                style={{
                  textShadow: "1px 1px 1px white, -1px -1px 1px white",
                }}
              >
                {cm}cm
              </text>
            </g>
          );
        })}
        {markers.map((marker) => (
          <image
            key={`thumb-marker-${marker.id}`}
            href={checkedImg}
            x={marker.x - 8}
            y={marker.y - 8}
            width="16"
            height="16"
          />
        ))}
      </svg>
    );
  };

  return (
    <Layout style={{ background: "#f8fafc", minHeight: "100vh" }}>
      <NeuroCareHeader selectedMenu={"검사데이터"} />
      {loading && <Loader />}

      <div
        style={{
          display: "flex",
          padding: "10px 40px 0px 40px",
          gap: "30px",
          justifyContent: "center",
          alignItems: "flex-start",
          maxWidth: "100%",
          margin: "0 auto",
          width: "100%",
          height: "calc(100vh - 60px)",
        }}
      >
        {!selectedTest ? (
          <>
            <div
              style={{
                width: "320px",
                flexShrink: 0,
                position: "sticky",
                top: "40px",
              }}
            >
              <ConfigProvider locale={locale}>
                <div className="custom-calendar-card">
                  <Calendar
                    value={dayjs(selectedDateView, "YYYYMMDD")}
                    fullscreen={false}
                    headerRender={calendarHeaderRender}
                    onSelect={handleDateChange}
                    onPanelChange={handlePanelChange}
                    dateCellRender={dateCellRender}
                  />
                </div>
              </ConfigProvider>
            </div>

            <div
              style={{
                flex: 1,
                maxWidth: "1000px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{ display: "flex", gap: "12px", marginBottom: "24px" }}
              >
                <Input
                  placeholder="환자명 입력"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onPressEnter={handleSearch}
                  style={{
                    flex: 1,
                    padding: "10px 16px",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    fontSize: "15px",
                  }}
                />
                <Button
                  type="primary"
                  onClick={handleSearch}
                  style={{
                    backgroundColor: "#2C6AFF",
                    borderRadius: "8px",
                    padding: "0 28px",
                    height: "44px",
                    fontSize: "15px",
                    fontWeight: "600",
                    border: "none",
                  }}
                >
                  검색
                </Button>
                <Button
                  onClick={handleRefresh}
                  style={{
                    borderRadius: "8px",
                    padding: "0 28px",
                    height: "44px",
                    fontSize: "15px",
                    fontWeight: "600",
                    color: "#64748b",
                    border: "1px solid #cbd5e1",
                    backgroundColor: "#fff",
                  }}
                >
                  새로고침
                </Button>
                {deleteMode && (
                  <Button
                    onClick={exitDeleteMode}
                    style={{
                      borderRadius: "8px",
                      padding: "0 28px",
                      height: "44px",
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "#64748b",
                      border: "1px solid #cbd5e1",
                      backgroundColor: "#fff",
                    }}
                  >
                    취소
                  </Button>
                )}
                <Button
                  onClick={deleteMode ? handleDeleteTests : enterDeleteMode}
                  style={{
                    borderRadius: "8px",
                    padding: "0 28px",
                    height: "44px",
                    fontSize: "15px",
                    fontWeight: "600",
                    color: "#fff",
                    border: "1px solid #DC2626",
                    backgroundColor: "#DC2626",
                  }}
                >
                  삭제
                </Button>
              </div>

              <div
                style={{ display: "flex", gap: "10px", marginBottom: "16px" }}
              >
                <div
                  onClick={() => setCompletionFilter("all")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "6px 16px",
                    borderRadius: "20px",
                    cursor: "pointer",
                    backgroundColor:
                      completionFilter === "all" ? "#1B1D2D" : "#ffffff",
                    color: completionFilter === "all" ? "#ffffff" : "#475569",
                    border:
                      completionFilter === "all"
                        ? "1px solid #1B1D2D"
                        : "1px solid #E3E8EF",
                    fontSize: "14px",
                    fontWeight: "600",
                    transition: "all 0.2s",
                  }}
                >
                  전체
                  <span
                    style={{
                      backgroundColor:
                        completionFilter === "all"
                          ? "rgba(255,255,255,0.2)"
                          : "#F1F5F9",
                      padding: "2px 8px",
                      borderRadius: "10px",
                      fontSize: "12px",
                    }}
                  >
                    {currentList.length}
                  </span>
                </div>
                <div
                  onClick={() => setCompletionFilter("incomplete")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "6px 16px",
                    borderRadius: "20px",
                    cursor: "pointer",
                    backgroundColor:
                      completionFilter === "incomplete" ? "#1B1D2D" : "#ffffff",
                    color:
                      completionFilter === "incomplete" ? "#ffffff" : "#475569",
                    border:
                      completionFilter === "incomplete"
                        ? "1px solid #1B1D2D"
                        : "1px solid #E3E8EF",
                    fontSize: "14px",
                    fontWeight: "600",
                    transition: "all 0.2s",
                  }}
                >
                  미완료
                  <span
                    style={{
                      backgroundColor:
                        completionFilter === "incomplete"
                          ? "rgba(255,255,255,0.2)"
                          : "#F1F5F9",
                      padding: "2px 8px",
                      borderRadius: "10px",
                      fontSize: "12px",
                    }}
                  >
                    {currentList.filter((item) => !item.isChecked).length}
                  </span>
                </div>
                <div
                  onClick={() => setCompletionFilter("complete")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "6px 16px",
                    borderRadius: "20px",
                    cursor: "pointer",
                    backgroundColor:
                      completionFilter === "complete" ? "#1B1D2D" : "#ffffff",
                    color:
                      completionFilter === "complete" ? "#ffffff" : "#475569",
                    border:
                      completionFilter === "complete"
                        ? "1px solid #1B1D2D"
                        : "1px solid #E3E8EF",
                    fontSize: "14px",
                    fontWeight: "600",
                    transition: "all 0.2s",
                  }}
                >
                  완료
                  <span
                    style={{
                      backgroundColor:
                        completionFilter === "complete"
                          ? "rgba(255,255,255,0.2)"
                          : "#F1F5F9",
                      padding: "2px 8px",
                      borderRadius: "10px",
                      fontSize: "12px",
                    }}
                  >
                    {currentList.filter((item) => item.isChecked).length}
                  </span>
                </div>
              </div>

              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  overflow: "hidden",
                  padding: "4px",
                }}
              >
                <Table
                  className="custom-patient-table"
                  columns={columns}
                  dataSource={filteredList}
                  rowKey={(record) => record.dateTime}
                  rowSelection={
                    deleteMode
                      ? {
                          selectedRowKeys,
                          onChange: (keys) => setSelectedRowKeys(keys),
                          columnWidth: 48,
                        }
                      : undefined
                  }
                  pagination={{ pageSize: 13, position: ["bottomCenter"] }}
                  bordered={false}
                  onRow={(record) => {
                    if (deleteMode) {
                      // 삭제 모드에서는 행 클릭이 체크박스 토글로 동작하고,
                      // 더블클릭으로 상세가 열리지 않게 한다.
                      return {
                        onClick: () => {
                          setSelectedRowKeys((prev) =>
                            prev.includes(record.dateTime)
                              ? prev.filter((k) => k !== record.dateTime)
                              : [...prev, record.dateTime],
                          );
                        },
                        style: { cursor: "pointer" },
                      };
                    }
                    return {
                      onDoubleClick: async () => {
                        const patientData = data.find(
                          (dt) => dt.id === record.patientId,
                        );
                        const testData = patientData?.tests.find(
                          (t) => t.id === record.dateTime,
                        );

                        if (testData) {
                          setGuideCheck(false);
                          setSelectedTest({
                            ...testData,
                            name: patientData?.name || "",
                          });

                          if (!record.isChecked) {
                            try {
                              const testRef = doc(
                                db,
                                "Accounts",
                                user.id,
                                "Patients",
                                record.patientId,
                                "Tests",
                                record.dateTime,
                              );
                              await updateDoc(testRef, { isChecked: true });

                              const updateItem = (item) =>
                                item.dateTime === record.dateTime &&
                                item.patientId === record.patientId
                                  ? { ...item, isChecked: true }
                                  : item;

                              setPatientList((prev) => prev.map(updateItem));
                              setCurrentList((prev) => prev.map(updateItem));
                              setFilteredList((prev) => prev.map(updateItem));
                            } catch (error) {
                              console.error("완료 상태 업데이트 실패", error);
                            }
                          }
                        }
                      },
                      style: { cursor: "pointer" },
                    };
                  }}
                />
              </div>
            </div>
          </>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              height: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingBottom: "10px",
                borderBottom: "1px solid #E2E8F0",
                marginBottom: "15px",
              }}
            >
              <div
                style={{ display: "flex", gap: "10px", alignItems: "center" }}
              >
                <div
                  onClick={() => {
                    if (isSaving) return;
                    setSelectedTest(undefined);
                  }}
                  style={{
                    cursor: isSaving ? "not-allowed" : "pointer",
                    opacity: isSaving ? 0.5 : 1,
                    border: "1px solid #E2E8F0",
                    background: "#fff",
                    borderRadius: "20px",
                    padding: "6px 16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#334155",
                  }}
                >
                  <img
                    src={expandLeftImg}
                    alt="back"
                    style={{ width: "6px" }}
                  />{" "}
                  목록으로
                </div>
                <div
                  style={{
                    border: "1px solid #E2E8F0",
                    background: "#fff",
                    borderRadius: "20px",
                    padding: "6px 16px",
                    fontSize: "10px",
                    color: "#64748B",
                  }}
                >
                  환자명{" "}
                  <strong
                    style={{
                      color: "#1E293B",
                      marginLeft: "6px",
                      fontSize: "12px",
                    }}
                  >
                    {patientName}
                  </strong>
                </div>
                <div
                  style={{
                    border: "1px solid #E2E8F0",
                    background: "#fff",
                    borderRadius: "20px",
                    padding: "6px 16px",
                    fontSize: "10px",
                    color: "#64748B",
                  }}
                >
                  환자번호{" "}
                  <strong
                    style={{
                      color: "#1E293B",
                      marginLeft: "6px",
                      fontSize: "12px",
                    }}
                  >
                    {patientNo}
                  </strong>
                </div>
                <div
                  style={{
                    color: "#94A3B8",
                    fontSize: "12px",
                    marginLeft: "8px",
                  }}
                >
                  검사 일자: {selectedTest.id}
                </div>
              </div>
              <div
                className="patient-info-actions"
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <div
                  onClick={() => setDiseaseModalOpen(true)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "6px 16px",
                    borderRadius: "20px",
                    border: "1px solid #E3E8EF",
                    backgroundColor: "#ffffff",
                    color: "#364153",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  상병코드
                </div>
                <div
                  onClick={() =>
                    guideCheckChanged({ target: { checked: !guideCheck } })
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "6px 16px",
                    borderRadius: "20px",
                    border: "1px solid #E3E8EF",
                    backgroundColor: "#ffffff",
                    color: "#364153",
                    fontSize: "12 px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  가이드
                </div>

                <div
                  style={{
                    display: "flex",
                    background: "#F1F5F9",
                    padding: "4px",
                    borderRadius: "24px",
                    gap: "4px",
                  }}
                >
                  <div
                    onClick={() => setActiveExamType("simple")}
                    style={{
                      cursor: "pointer",
                      padding: "6px 16px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: "600",
                      backgroundColor:
                        activeExamType === "simple" ? "#ffffff" : "transparent",
                      color:
                        activeExamType === "simple" ? "#2C6AFF" : "#818C9E",
                      boxShadow:
                        activeExamType === "simple"
                          ? "0 1px 3px rgba(0,0,0,0.05)"
                          : "none",
                      transition: "all 0.2s ease",
                    }}
                  >
                    단순신경학적검사
                  </div>
                  <div
                    onClick={() => setActiveExamType("general")}
                    style={{
                      cursor: "pointer",
                      padding: "6px 16px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: "600",
                      backgroundColor:
                        activeExamType === "general"
                          ? "#ffffff"
                          : "transparent",
                      color:
                        activeExamType === "general" ? "#2C6AFF" : "#818C9E",
                      boxShadow:
                        activeExamType === "general"
                          ? "0 1px 3px rgba(0,0,0,0.05)"
                          : "none",
                      transition: "all 0.2s ease",
                    }}
                  >
                    일반신경학적검사
                  </div>
                </div>
              </div>
            </div>

            <div
              id="pdf-report-area"
              style={{
                flex: 1,
                overflowY: "auto",
                overflowX: "auto",
                display: "flex",
                gap: "24px",
                paddingBottom: "40px",
              }}
            >
              <div
                id="pdf-left-panel"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                  width: "684px",
                  flexShrink: 0,
                }}
              >
                <div
                  id="pdf-left-section-1"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                  }}
                >
                <div
                  style={{
                    display: "flex",
                    gap: "24px",
                    alignItems: "stretch",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "24px",
                      width: "360px",
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #E3E8EF",
                        borderRadius: "12px",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      <div
                        style={{
                          padding: "8px 16px",
                          background: "#F8FAFC",
                          borderBottom: "1px solid #E3E8EF",
                          fontSize: "11px",
                          fontWeight: "bold",
                          color: "#818C9E",
                          textAlign: "left",
                        }}
                      >
                        검사 개요
                      </div>
                      <div
                        style={{
                          padding: "24px",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            marginBottom: "24px",
                          }}
                        >
                          {selectedTest && radarChartElement}
                        </div>
                        {selectedTest && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "10px",
                            }}
                          >
                            <div
                              style={{
                                background: "#F8FAFC",
                                border: "1px solid #E2E8F0",
                                borderRadius: "8px",
                                padding: "12px 16px",
                                fontSize: "10px",
                                color: "#475569",
                                textAlign: "left",
                              }}
                            >
                              얼굴대칭: {avgAvgRatio}% (상관: {avgEyeRatio}%,
                              하관: {avgMouthRatio}%){" "}
                              {isFaceNonTest ? (
                                <span
                                  style={{ color: "red", fontWeight: "bold" }}
                                >
                                  Non Test
                                </span>
                              ) : (
                                <span
                                  style={{
                                    color:
                                      isSmileText === "Non Smile"
                                        ? "red"
                                        : "inherit",
                                    fontWeight:
                                      isSmileText === "Non Smile"
                                        ? "bold"
                                        : "normal",
                                  }}
                                >
                                  {isSmileText}
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                background: "#F8FAFC",
                                border: "1px solid #E2E8F0",
                                borderRadius: "8px",
                                padding: "12px 16px",
                                fontSize: "10px",
                                color: "#475569",
                                textAlign: "left",
                              }}
                            >
                              동공운동: {avgPupilData}% ({pass_count}/
                              {total_count}) 왼쪽 {leftFailCount}회 실패, 오른쪽{" "}
                              {rightFailCount}회 실패{" "}
                              {isPupilNonTest && (
                                <span
                                  style={{ color: "red", fontWeight: "bold" }}
                                >
                                  Non Test
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                background: "#F8FAFC",
                                border: "1px solid #E2E8F0",
                                borderRadius: "8px",
                                padding: "12px 16px",
                                fontSize: "10px",
                                color: "#475569",
                                textAlign: "left",
                              }}
                            >
                              왼팔편측: GRADE{" "}
                              {isLeftMotorNonTest ? "-" : leftMotorGrade} (평균
                              각도: {leftAngleAvgData.toFixed(1)}°){" "}
                              {isLeftMotorNonTest && (
                                <span
                                  style={{ color: "red", fontWeight: "bold" }}
                                >
                                  Non Test
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                background: "#F8FAFC",
                                border: "1px solid #E2E8F0",
                                borderRadius: "8px",
                                padding: "12px 16px",
                                fontSize: "10px",
                                color: "#475569",
                                textAlign: "left",
                              }}
                            >
                              오른팔편측: GRADE{" "}
                              {isRightMotorNonTest ? "-" : rightMotorGrade}{" "}
                              (평균 각도: {rightAngleAvgData.toFixed(1)}°){" "}
                              {isRightMotorNonTest && (
                                <span
                                  style={{ color: "red", fontWeight: "bold" }}
                                >
                                  Non Test
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                background: "#F8FAFC",
                                border: "1px solid #E2E8F0",
                                borderRadius: "8px",
                                padding: "12px 16px",
                                fontSize: "10px",
                                color: "#475569",
                                textAlign: "left",
                              }}
                            >
                              언어능력: {avgSpeechData}% (정확도:{" "}
                              {speechAccuracy}%, 유창성: {speechFluency}%,
                              완성도: {speechCompleteness}%){" "}
                              {isSpeechNonTest && (
                                <span
                                  style={{ color: "red", fontWeight: "bold" }}
                                >
                                  Non Test
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #E3E8EF",
                        borderRadius: "12px",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        flexShrink: 0,
                        marginTop: "-13px",
                      }}
                    >
                      <div
                        style={{
                          padding: "8px 16px",
                          background: "#F8FAFC",
                          borderBottom: "1px solid #E3E8EF",
                          fontSize: "11px",
                          fontWeight: "bold",
                          color: "#818C9E",
                          textAlign: "left",
                        }}
                      >
                        혈압 데이터
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "16px",
                          padding: "24px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "10px",
                              color: "#64748B",
                              marginBottom: "8px",
                              fontWeight: "600",
                            }}
                          >
                            이완기 혈압
                          </div>
                          <Input
                            suffix={
                              <span style={{ color: "#818C9E" }}>mmHg</span>
                            }
                            value={bpDiastolic}
                            onChange={(e) => setBpDiastolic(e.target.value)}
                            style={{
                              borderRadius: "8px",
                              background: "#F8FAFC",
                            }}
                            placeholder="0"
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "10px",
                              color: "#64748B",
                              marginBottom: "8px",
                              fontWeight: "600",
                            }}
                          >
                            수축기 혈압
                          </div>
                          <Input
                            suffix={
                              <span style={{ color: "#818C9E" }}>mmHg</span>
                            }
                            value={bpSystolic}
                            onChange={(e) => setBpSystolic(e.target.value)}
                            placeholder="0"
                            style={{
                              borderRadius: "8px",
                              background: "#F8FAFC",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "24px",
                      width: "300px",
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #E3E8EF",
                        borderRadius: "12px",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        flexShrink: 0,
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "8px 16px",
                          background: "#F8FAFC",
                          borderBottom: "1px solid #E3E8EF",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: "bold",
                            color: "#818C9E",
                          }}
                        >
                          양팔편측
                        </span>
                        <div style={{ display: "flex", gap: "16px" }}>
                          <img
                            src={
                              armChartView === "left"
                                ? leftArrowGray
                                : leftArrowBlack
                            }
                            alt="left"
                            style={{
                              cursor:
                                armChartView === "left" ? "default" : "pointer",
                              width: "8px",
                            }}
                            onClick={() => {
                              if (armChartView !== "left")
                                setArmChartView("left");
                            }}
                          />
                          <img
                            src={
                              armChartView === "right"
                                ? rightArrowGray
                                : rightArrowBlack
                            }
                            alt="right"
                            style={{
                              cursor:
                                armChartView === "right"
                                  ? "default"
                                  : "pointer",
                              width: "8px",
                            }}
                            onClick={() => {
                              if (armChartView !== "right")
                                setArmChartView("right");
                            }}
                          />
                        </div>
                      </div>

                      <div
                        style={{
                          position: "relative",
                          padding: "16px 20px",
                          display: "flex",
                          flexDirection: "column",
                          height: "260px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            position: "relative",
                            zIndex: 10,
                          }}
                        >
                          <span
                            style={{
                              backgroundColor: currentFillColor,
                              color: currentLineColor,
                              padding: "4px 14px",
                              borderRadius: "20px",
                              fontSize: "10px",
                              fontWeight: "bold",
                            }}
                          >
                            {currentBadgeText}
                          </span>
                        </div>

                        <div
                          style={{
                            position: "relative",
                            flex: 1,
                            marginTop: "10px",
                          }}
                        >
                          <img
                            src={armImg}
                            alt="arm background"
                            style={{
                              position: "absolute",
                              left: "40px",
                              top: "10px",
                              bottom: "30px",
                              height: "calc(100% - 40px)",
                              pointerEvents: "none",
                              zIndex: 1,
                            }}
                          />
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart
                              data={currentChartData}
                              margin={{
                                top: 10,
                                right: 10,
                                left: 0,
                                bottom: 0,
                              }}
                              style={{ zIndex: 2, position: "relative" }}
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={true}
                                horizontal={true}
                                stroke="#E2E8F0"
                              />
                              <XAxis
                                dataKey="time"
                                type="number"
                                domain={[0, chartMaxX]}
                                ticks={xTicks}
                                tickFormatter={(val) => `${val}s`}
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 8, fill: "#BABABA", dy: 5 }}
                                height={30}
                              />
                              <YAxis
                                width={35}
                                domain={[0, 180]}
                                ticks={[0, 30, 60, 90, 120, 150, 180]}
                                tickFormatter={(val) => `${val}°`}
                                axisLine={false}
                                tickLine={false}
                                tick={{
                                  textAnchor: "start",
                                  dx: -20,
                                  fontSize: 8,
                                  fill: "#BABABA",
                                }}
                              />
                              <Area
                                type="monotone"
                                dataKey="angle"
                                stroke={currentLineColor}
                                strokeWidth={2}
                                fill={currentFillColor}
                                isAnimationActive={false}
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      <div
                        onClick={() => setArmModalOpen(true)}
                        style={{
                          padding: "8px 16px",
                          backgroundColor: "#F8FAFC",
                          borderTop: "1px solid #E2E8F0",
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: "bold",
                            color: "#818C9E",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          전체보기
                          <img
                            src={arrowDownImg}
                            alt="arrow down"
                            style={{
                              width: "16px",
                              height: "16px",
                              marginLeft: "4px",
                            }}
                          />
                        </span>
                      </div>
                    </div>

                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #E3E8EF",
                        borderRadius: "12px",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        flexShrink: 0,
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          padding: "8px 16px",
                          background: "#F8FAFC",
                          borderBottom: "1px solid #E3E8EF",
                          fontSize: "11px",
                          fontWeight: "bold",
                          color: "#818C9E",
                          textAlign: "left",
                        }}
                      >
                        얼굴대칭
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "24px",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "24px",
                          flex: 1,
                        }}
                      >
                        <div
                          className="face-image-circle"
                          onDoubleClick={() => openImagePreview(faceImage1)}
                          style={{
                            position: "relative",
                            width: "160px",
                            height: "160px",
                            border: "1px solid #E2E8F0",
                            borderRadius: "50%",
                            overflow: "hidden",
                          }}
                        >
                          {faceImage1 ? (
                            <>
                              <img
                                src={faceImage1}
                                alt="얼굴 사진 1"
                                crossOrigin="anonymous"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  display: "block",
                                }}
                              />
                              {renderThumbnailAnnotations(faceImage1)}
                            </>
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#F8FAFC",
                                color: "#94A3B8",
                              }}
                            >
                              No Image
                            </div>
                          )}
                        </div>
                        <div
                          className="face-image-circle"
                          onDoubleClick={() => openImagePreview(faceImage2)}
                          style={{
                            position: "relative",
                            width: "160px",
                            height: "160px",
                            border: "1px solid #E2E8F0",
                            borderRadius: "50%",
                            overflow: "hidden",
                          }}
                        >
                          {faceImage2 ? (
                            <>
                              <img
                                src={faceImage2}
                                alt="얼굴 사진 2"
                                crossOrigin="anonymous"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  display: "block",
                                }}
                              />
                              {renderThumbnailAnnotations(faceImage2)}
                            </>
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#F8FAFC",
                                color: "#94A3B8",
                              }}
                            >
                              No Image
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </div>

                {/* PDF 2페이지: 확장검사 + 비고 */}
                <div
                  id="pdf-left-section-2"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                  }}
                >

                {/* ================= 확장검사 ================= */}
                {extendedActive &&
                  (() => {
                    const oData = selectedTest?.orientation_data;
                    const mcList = oData?.mc_answers
                      ? Array.isArray(oData.mc_answers)
                        ? oData.mc_answers
                        : Object.values(oData.mc_answers)
                      : [];
                    const cData = selectedTest?.cerebellar_data;
                    const gData = selectedTest?.hand_grip_data;
                    const oPct =
                      oData && oData.total_count > 0
                        ? Math.round((oData.pass_count / oData.total_count) * 100)
                        : 0;
                    return (
                <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "4px 0",
                  }}
                >
                  <div style={{ flex: 1, height: "1px", background: "#eef0f2" }} />
                  <span
                    style={{
                      fontSize: "10px",
                      fontWeight: 700,
                      color: "#16a34a",
                      background: "#eafaf0",
                      padding: "2px 8px",
                      borderRadius: "10px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    확장검사 활성화됨 (인증 병원)
                  </span>
                  <div style={{ flex: 1, height: "1px", background: "#eef0f2" }} />
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #e4e7ec",
                      borderRadius: "10px",
                      padding: "16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "space-between",
                      }}
                    >
                      <span style={{ fontSize: "13px", fontWeight: 700 }}>
                        지남력 검사
                      </span>
                      <span style={{ fontSize: "10px", color: "#9aa1ac" }}>
                        질의응답 + STT 성명 확인
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      {mcList.length > 0 ? (
                        mcList.map((mc, i) => (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: "8px",
                              fontSize: "12px",
                            }}
                          >
                            <span style={{ color: "#4b5563" }}>
                              {mc.question}
                            </span>
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                flexShrink: 0,
                              }}
                            >
                              <span
                                style={{
                                  color: "#9aa1ac",
                                  fontWeight: 500,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                ({mc.selected_answer ?? "-"})
                              </span>
                              <span
                                style={{
                                  fontSize: "10px",
                                  fontWeight: 700,
                                  color: mc.is_correct ? "#16a34a" : "#dc2626",
                                  background: mc.is_correct
                                    ? "#eafaf0"
                                    : "#fdecec",
                                  padding: "2px 8px",
                                  borderRadius: "10px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {mc.is_correct ? "정답" : "오답"}
                              </span>
                            </span>
                          </div>
                        ))
                      ) : (
                        <div style={{ fontSize: "12px", color: "#9aa1ac" }}>
                          질의응답 데이터 없음
                        </div>
                      )}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          fontSize: "12px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "2px",
                              height: "16px",
                            }}
                          >
                            {[8, 14, 16, 11, 6].map((h, i) => (
                              <div
                                key={i}
                                style={{
                                  width: "3px",
                                  height: `${h}px`,
                                  background: "#2f6fed",
                                  borderRadius: "1px",
                                }}
                              />
                            ))}
                          </div>
                          <span style={{ color: "#1f2430", fontWeight: 600 }}>
                            "{oData?.name_answer || "-"}"
                          </span>
                        </div>
                      </div>
                    </div>
                    <div style={{ height: "1px", background: "#eef0f2" }} />
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          height: "6px",
                          background: "#eef0f2",
                          borderRadius: "3px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${oPct}%`,
                            height: "100%",
                            background: "#2f6fed",
                          }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {oData
                          ? `${oData.pass_count ?? 0}/${oData.total_count ?? 0} (${oPct}%)`
                          : "-"}
                      </span>
                    </div>
                  </div>
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #e4e7ec",
                      borderRadius: "10px",
                      padding: "16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "space-between",
                      }}
                    >
                      <span style={{ fontSize: "13px", fontWeight: 700 }}>
                        설측검사
                      </span>
                      <span style={{ fontSize: "10px", color: "#9aa1ac" }}>
                        혀 중앙선 대비 편위 측정
                      </span>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "36px",
                      }}
                    >
                    <div
                      onClick={() => tongueImage && openImagePreview(tongueImage)}
                      style={{
                        position: "relative",
                        flexShrink: 0,
                        width: "132px",
                        height: "132px",
                        cursor: tongueImage ? "pointer" : "default",
                        borderRadius: "50%",
                        overflow: "hidden",
                        background:
                          "repeating-linear-gradient(135deg,#f1f2f4,#f1f2f4 6px,#e7e9ec 6px,#e7e9ec 12px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {tongueImage ? (
                        <img
                          src={tongueImage}
                          alt="설측검사 캡처"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <span
                          style={{
                            fontFamily: "ui-monospace, monospace",
                            fontSize: "8px",
                            color: "#9aa1ac",
                            textAlign: "center",
                            lineHeight: 1.3,
                            padding: "0 12px",
                          }}
                        >
                          촬영 이미지
                          <br />
                          없음
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: 700,
                          color: "#6b7280",
                        }}
                      >
                        판정
                      </span>
                      {[
                        { value: "left", label: "왼쪽" },
                        { value: "center", label: "중앙" },
                        { value: "right", label: "오른쪽" },
                      ].map((opt) => {
                        const selected =
                          selectedTest?.tongue_judgment === opt.value;
                        const isNormal = opt.value === "center";
                        return (
                          <span
                            key={opt.value}
                            onClick={async () => {
                              const newValue = selected ? null : opt.value;
                              try {
                                const testRef = doc(
                                  db,
                                  "Accounts",
                                  user.id,
                                  "Patients",
                                  selectedTest.patientId,
                                  "Tests",
                                  selectedTest.id,
                                );
                                await updateDoc(testRef, {
                                  tongue_judgment: newValue,
                                });
                                setSelectedTest((prev) => ({
                                  ...prev,
                                  tongue_judgment: newValue,
                                }));
                              } catch (e) {
                                console.error("설측검사 판정 저장 실패:", e);
                                alert("판정 저장에 실패했습니다.");
                              }
                            }}
                            style={{
                              fontSize: "13px",
                              fontWeight: 700,
                              cursor: "pointer",
                              userSelect: "none",
                              color: selected
                                ? isNormal
                                  ? "#16a34a"
                                  : "#dc2626"
                                : "#6b7280",
                              background: selected
                                ? isNormal
                                  ? "#eafaf0"
                                  : "#fdecec"
                                : "#f1f2f4",
                              border: selected
                                ? `1px solid ${isNormal ? "#16a34a" : "#dc2626"}`
                                : "1px solid transparent",
                              padding: "3px 0",
                              width: "64px",
                              textAlign: "center",
                              borderRadius: "10px",
                            }}
                          >
                            {opt.label}
                          </span>
                        );
                      })}
                    </div>
                    </div>
                  </div>
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #e4e7ec",
                      borderRadius: "10px",
                      padding: "16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "space-between",
                      }}
                    >
                      <span style={{ fontSize: "13px", fontWeight: 700 }}>
                        소뇌협응검사
                      </span>
                      <span style={{ fontSize: "10px", color: "#9aa1ac" }}>
                        코-모니터 교대 지지검사
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        padding: "4px 0",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "32px",
                          alignItems: "flex-end",
                        }}
                      >
                      {(() => {
                        const bars = [
                          {
                            success: cData?.left_success_count,
                            total: cData?.left_total_tries,
                            color: "#2f6fed",
                          },
                          {
                            success: cData?.right_success_count,
                            total: cData?.right_total_tries,
                            color: "#f59e0b",
                          },
                        ];
                        return (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "3px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "10px",
                                fontWeight: 700,
                                color: "#6b7280",
                                textAlign: "center",
                              }}
                            >
                              성공 횟수
                            </span>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "flex-end",
                                gap: "36px",
                                borderBottom: "1px solid #e4e7ec",
                              }}
                            >
                              {bars.map((bar, i) => (
                                <div
                                  key={i}
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: "3px",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      fontWeight: 700,
                                      color: "#1f2430",
                                    }}
                                  >
                                    {bar.success != null && bar.total != null
                                      ? `${bar.success}/${bar.total}`
                                      : "-"}
                                  </span>
                                  <div
                                    style={{
                                      width: "28px",
                                      height: "44px",
                                      background: "#eef0f2",
                                      borderRadius: "4px 4px 0 0",
                                      display: "flex",
                                      alignItems: "flex-end",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "100%",
                                        height: `${
                                          bar.success != null && bar.total > 0
                                            ? (bar.success / bar.total) * 100
                                            : 0
                                        }%`,
                                        background: bar.color,
                                      }}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                      <div
                        style={{
                          width: "1px",
                          alignSelf: "stretch",
                          background: "#e4e7ec",
                        }}
                      />
                      {(() => {
                        const rLeft = cData?.left_avg_reaction_ms;
                        const rRight = cData?.right_avg_reaction_ms;
                        const rMax = Math.max(rLeft ?? 0, rRight ?? 0, 1);
                        const fmt = (v) =>
                          `${parseFloat((v / 1000).toFixed(3))}s`;
                        return (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "3px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "10px",
                                fontWeight: 700,
                                color: "#6b7280",
                                textAlign: "center",
                              }}
                            >
                              반응속도
                            </span>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "flex-end",
                                gap: "36px",
                                borderBottom: "1px solid #e4e7ec",
                              }}
                            >
                              {[
                                { value: rLeft, color: "#2f6fed" },
                                { value: rRight, color: "#f59e0b" },
                              ].map((bar, i) => (
                                <div
                                  key={i}
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: "3px",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      fontWeight: 700,
                                      color: "#1f2430",
                                    }}
                                  >
                                    {bar.value != null ? fmt(bar.value) : "-"}
                                  </span>
                                  <div
                                    style={{
                                      width: "28px",
                                      height: "44px",
                                      display: "flex",
                                      alignItems: "flex-end",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "100%",
                                        height: `${bar.value != null ? (bar.value / rMax) * 100 : 0}%`,
                                        background: bar.color,
                                        borderRadius: "4px 4px 0 0",
                                      }}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          fontSize: "10px",
                          color: "#6b7280",
                          paddingLeft: "64px",
                          paddingRight: "64px",
                        }}
                      >
                        <span
                          style={{
                            flex: 1,
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span
                            style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "2px",
                              background: "#2f6fed",
                              display: "inline-block",
                            }}
                          />
                          왼손
                        </span>
                        <span
                          style={{
                            flex: 1,
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span
                            style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "2px",
                              background: "#f59e0b",
                              display: "inline-block",
                            }}
                          />
                          오른손
                        </span>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: "6px",
                        textAlign: "center",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: "13px", fontWeight: 700 }}>
                          {cData?.left_avg_accuracy != null
                            ? `${parseFloat(Number(cData.left_avg_accuracy).toFixed(1))}%`
                            : "-"}
                        </div>
                        <div style={{ fontSize: "9px", color: "#9aa1ac" }}>왼손 정확도</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "13px", fontWeight: 700 }}>
                          {cData?.right_avg_accuracy != null
                            ? `${parseFloat(Number(cData.right_avg_accuracy).toFixed(1))}%`
                            : "-"}
                        </div>
                        <div style={{ fontSize: "9px", color: "#9aa1ac" }}>오른손 정확도</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "13px", fontWeight: 700 }}>
                          {cData?.left_avg_reaction_ms != null &&
                          cData?.right_avg_reaction_ms != null
                            ? `${parseFloat(
                                (
                                  (cData.left_avg_reaction_ms +
                                    cData.right_avg_reaction_ms) /
                                  2000
                                ).toFixed(3)
                              )}s`
                            : "-"}
                        </div>
                        <div style={{ fontSize: "9px", color: "#9aa1ac" }}>평균 반응속도</div>
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #e4e7ec",
                      borderRadius: "10px",
                      padding: "16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "space-between",
                      }}
                    >
                      <span style={{ fontSize: "13px", fontWeight: 700 }}>
                        손쥐었다펴기검사
                      </span>
                      <span style={{ fontSize: "10px", color: "#9aa1ac" }}>
                        양손 개방-폐쇄 리듬 측정
                      </span>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        gap: "20px",
                        padding: "12px 0",
                      }}
                    >
                      {(() => {
                        const gLeft = gData?.left_count;
                        const gRight = gData?.right_count;
                        const max = Math.max(gLeft ?? 0, gRight ?? 0, 1);
                        const leftPct =
                          gLeft != null ? (gLeft / max) * 100 : 0;
                        const rightPct =
                          gRight != null ? (gRight / max) * 100 : 0;
                        return (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "10px",
                                fontWeight: 700,
                                color: "#6b7280",
                                textAlign: "center",
                                paddingLeft: "64px",
                                paddingRight: "64px",
                              }}
                            >
                              반복 횟수
                            </span>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                fontSize: "12px",
                              }}
                            >
                              <span
                                style={{
                                  width: "48px",
                                  flexShrink: 0,
                                  textAlign: "center",
                                  fontWeight: 700,
                                  fontSize: "13px",
                                  color: "#1f2430",
                                  marginRight: "16px",
                                }}
                              >
                                {gLeft != null ? `${gLeft}회` : "-"}
                              </span>
                              <div
                                style={{
                                  flex: 1,
                                  height: "14px",
                                  background: "#eef0f2",
                                  borderRadius: "7px 0 0 7px",
                                  overflow: "hidden",
                                  display: "flex",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <div
                                  style={{
                                    width: `${leftPct}%`,
                                    height: "100%",
                                    background: "#2f6fed",
                                    borderRadius: "7px 0 0 7px",
                                  }}
                                />
                              </div>
                              <div
                                style={{
                                  width: "2px",
                                  height: "20px",
                                  background: "#9aa1ac",
                                  flexShrink: 0,
                                }}
                              />
                              <div
                                style={{
                                  flex: 1,
                                  height: "14px",
                                  background: "#eef0f2",
                                  borderRadius: "0 7px 7px 0",
                                  overflow: "hidden",
                                }}
                              >
                                <div
                                  style={{
                                    width: `${rightPct}%`,
                                    height: "100%",
                                    background: "#f59e0b",
                                    borderRadius: "0 7px 7px 0",
                                  }}
                                />
                              </div>
                              <span
                                style={{
                                  width: "48px",
                                  flexShrink: 0,
                                  textAlign: "center",
                                  fontWeight: 700,
                                  fontSize: "13px",
                                  color: "#1f2430",
                                  marginLeft: "16px",
                                }}
                              >
                                {gRight != null ? `${gRight}회` : "-"}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          fontSize: "10px",
                          color: "#6b7280",
                          paddingLeft: "64px",
                          paddingRight: "64px",
                        }}
                      >
                        <span
                          style={{
                            flex: 1,
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span
                            style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "2px",
                              background: "#2f6fed",
                              display: "inline-block",
                            }}
                          />
                          왼손
                        </span>
                        <span
                          style={{
                            flex: 1,
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span
                            style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "2px",
                              background: "#f59e0b",
                              display: "inline-block",
                            }}
                          />
                          오른손
                        </span>
                      </div>
                    </div>
                    <div style={{ height: "1px", background: "#eef0f2" }} />
                    <div style={{ textAlign: "center", padding: "4px 0" }}>
                      <div style={{ fontSize: "16px", fontWeight: 700 }}>
                        {gData
                          ? `${gData.left_count ?? "-"} / ${gData.right_count ?? "-"}회`
                          : "-"}
                      </div>
                      <div style={{ fontSize: "10px", color: "#9aa1ac", marginTop: "2px" }}>
                        반복 횟수 (좌/우)
                      </div>
                    </div>
                  </div>
                </div>
                </>
                    );
                  })()}
                {/* ================= 확장검사 끝 ================= */}

                <div
                  style={{
                    background: "#fff",
                    border: "1px solid #E3E8EF",
                    borderRadius: "12px",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    flexShrink: 0,
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      padding: "8px 16px",
                      background: "#F8FAFC",
                      borderBottom: "1px solid #E3E8EF",
                      fontSize: "11px",
                      fontWeight: "bold",
                      color: "#818C9E",
                      textAlign: "left",
                    }}
                  >
                    비고
                  </div>
                  <div style={{ padding: "24px" }}>
                    <Input.TextArea
                      rows={4}
                      value={remarksText}
                      onChange={(e) => setRemarksText(e.target.value)}
                      style={{
                        border: "none",
                        background: "transparent",
                        resize: "none",
                        padding: 0,
                      }}
                    />
                  </div>
                </div>
                </div>
              </div>

              <div
                id="pdf-right-panel"
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minWidth: "650px",
                }}
              >
                <div
                  style={{
                    background: "#fff",
                    border: "1px solid #E2E8F0",
                    borderRadius: "12px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    id="pdf-hide-top"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "5px 24px",
                      borderBottom: "1px solid #E2E8F0",
                      background: "#F8FAFC",
                      borderRadius: "12px 12px 0 0",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: "bold",
                        color: "#364153",
                      }}
                    >
                      {activeExamType === "simple"
                        ? "단순신경학적검사"
                        : "일반신경학적검사"}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <div
                        onClick={handleCheckAllNormal}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          cursor: "pointer",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "11px",
                            color: isAllChecked ? "#2C6AFF" : "#64748B",
                            fontWeight: "600",
                            userSelect: "none",
                          }}
                        >
                          {isAllChecked ? "전체 체크 해제" : "전체 정상 체크"}
                        </span>
                        <div
                          style={{
                            width: "44px",
                            height: "24px",
                            borderRadius: "12px",
                            backgroundColor: isAllChecked
                              ? "#2C6AFF"
                              : "#E2E8F0",
                            position: "relative",
                            transition: "background-color 0.2s ease",
                          }}
                        >
                          <div
                            style={{
                              width: "20px",
                              height: "20px",
                              borderRadius: "50%",
                              backgroundColor: "#fff",
                              position: "absolute",
                              top: "2px",
                              left: isAllChecked ? "22px" : "2px",
                              transition: "left 0.2s ease",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                            }}
                          />
                        </div>
                      </div>

                      <img
                        src={settingImg}
                        alt="설정"
                        style={{
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                        }}
                        onClick={() => setSettingModalOpen(true)}
                      />
                    </div>
                  </div>
                  <div
                    id="pdf-table-title"
                    style={{
                      display: "none",
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: "#364153",
                      textAlign: "center",
                      marginBottom: "24px",
                      paddingTop: "20px",
                    }}
                  >
                    {activeExamType === "simple"
                      ? "단순신경학적 검사표"
                      : "일반신경학적 검사표"}
                  </div>
                  <div
                    style={{
                      padding: "0",
                      backgroundColor: "#fff",
                    }}
                  >
                    {selectedTest && renderNeuroTable()}
                  </div>
                  <div
                    id="pdf-legal-text"
                    style={{
                      display: "none",
                      marginTop: "24px",
                      padding: "16px",
                      backgroundColor: "#F8FAFC",
                      border: "1px solid #E2E8F0",
                      borderRadius: "8px",
                      fontSize: "12px",
                      fontWeight: "500",
                      color: "#64748B",
                      textAlign: "center",
                      wordBreak: "keep-all",
                    }}
                  >
                    보건복지부 고시 제2023-134호에 의거하여 MRI 수가 산정의 필수
                    요건인 나610나 신경학적검사(일반검사) 항목을 포함하고
                    있으며, 의료진 분석을 통한 객관적 데이터를 증빙함.
                  </div>
                  {selectedTest && (
                    <div
                      id="pdf-hide-bottom"
                      style={{
                        padding: "12px 24px",
                        backgroundColor: "#F8FAFC",
                        borderTop: "1px solid #E2E8F0",
                        borderRadius: "0 0 12px 12px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div
                        onClick={handleReportAttachToggle}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                        }}
                      >
                        <div
                          style={{
                            width: "14px",
                            height: "14px",
                            borderRadius: "4px",
                            backgroundColor: isReportAttached
                              ? "#364153"
                              : "#fff",
                            border: `1px solid ${isReportAttached ? "#364153" : "#CBD5E1"}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.2s",
                          }}
                        >
                          {isReportAttached && (
                            <svg
                              width="10"
                              height="8"
                              viewBox="0 0 10 8"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1 4L3.5 6.5L9 1"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>
                        <span
                          style={{
                            fontSize: "11px",
                            color: "#364153",
                            fontWeight: "600",
                            userSelect: "none",
                          }}
                        >
                          소견서 첨부
                        </span>
                      </div>

                      <span
                        onClick={() => setReportModalOpen(true)}
                        style={{
                          fontSize: "11px",
                          fontWeight: "bold",
                          color: "#364153",
                          cursor: isReportAttached ? "pointer" : "default",
                          opacity: isReportAttached ? 1 : 0.4,
                          visibility: isReportAttached ? "visible" : "hidden",
                          transition: "opacity 0.2s",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        소견서보기
                        <img
                          src={arrowDownImg}
                          alt="arrow down"
                          style={{ width: "16px", height: "16px" }}
                        />
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div
                id="pdf-report-panel"
                style={{
                  display: "none",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  zIndex: -1,
                  width: "1000px",
                  boxSizing: "border-box",
                  padding: "40px",
                  backgroundColor: "#fff",
                  color: "#000",
                }}
              >
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: "#364153",
                    marginBottom: "30px",
                    borderBottom: "2px solid #364153",
                    paddingBottom: "10px",
                  }}
                >
                  {activeExamType === "simple"
                    ? "단순신경학적 소견서"
                    : "일반신경학적 소견서"}
                </div>
                <div
                  style={{
                    width: "100%",
                    wordBreak: "keep-all",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {renderReportModalContent()}
                </div>
              </div>
            </div>

            <div
              id="pdf-save-button"
              style={{
                position: "sticky",
                bottom: 0,
                backgroundColor: "#FFFFFF",
                margin: "0 -40px",
                padding: "12px 40px 16px 40px",
                borderTop: "1px solid #E2E8F0",
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                zIndex: 10,
              }}
            >
              {saveToastVisible && (
                <div
                  style={{
                    position: "absolute",
                    top: "-50px",
                    right: "40px",
                    backgroundColor: "#1B1D2D",
                    color: "#fff",
                    padding: "10px 16px",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: "500",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    zIndex: 100,
                    transition: "opacity 0.3s ease",
                  }}
                >
                  저장되었습니다.
                </div>
              )}
              {copyToastVisible && (
                <div
                  style={{
                    position: "absolute",
                    top: "-50px",
                    right: "40px",
                    backgroundColor: "#1B1D2D",
                    color: "#fff",
                    padding: "10px 16px",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: "500",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    zIndex: 100,
                    transition: "opacity 0.3s ease",
                  }}
                >
                  클립보드에 복사되었습니다.
                </div>
              )}
              <Button
                onClick={exportLeftViewToPdf}
                loading={isExporting}
                style={{
                  borderRadius: "8px",
                  padding: "0 24px",
                  height: "40px",
                  fontSize: "14px",
                  fontWeight: "600",
                  borderColor: "#E3E8EF",
                  color: "#475569",
                }}
              >
                내보내기
              </Button>

              <Button
                onClick={handleCopy}
                style={{
                  borderRadius: "8px",
                  padding: "0 24px",
                  height: "40px",
                  fontSize: "14px",
                  fontWeight: "600",
                  borderColor: "#C4DBFB",
                  backgroundColor: "#F0F6FE",
                }}
              >
                복사
              </Button>
              <Button
                type="primary"
                onClick={handleSaveReport}
                loading={isSaving}
                style={{
                  borderRadius: "8px",
                  padding: "0 24px",
                  height: "40px",
                  fontSize: "14px",
                  fontWeight: "600",
                  backgroundColor: "#2C6AFF",
                }}
              >
                저장
              </Button>
            </div>
          </div>
        )}
      </div>

      <Modal
        open={settingModalOpen}
        onCancel={handleCancelSettings}
        footer={null}
        width={1200}
        centered
        closeIcon={false}
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingRight: "0px",
            }}
          >
            <span
              style={{ fontSize: "12px", fontWeight: "bold", color: "#1E293B" }}
            >
              보기 설정
            </span>
            <span
              style={{
                backgroundColor: "#EFF6FF",
                color: "#2C6AFF",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "11px",
                fontWeight: "bold",
              }}
            >
              {viewMode === "full" ? "전체 보기" : "간략 보기"}
            </span>
          </div>
        }
        styles={{
          header: {
            padding: "10px 24px",
            borderBottom: "1px solid #E2E8F0",
            margin: 0,
          },
          body: { padding: 0 },
        }}
      >
        <div style={{ display: "flex", height: "600px" }}>
          <div
            style={{
              width: "260px",
              borderRight: "1px solid #E2E8F0",
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              overflowY: "auto",
              backgroundColor: "#F8FAFC",
            }}
          >
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: "bold",
                  color: "#818C9E",
                }}
              >
                보기 모드
              </div>
              <div
                style={{
                  display: "flex",
                  backgroundColor: "#F1F5F9",
                  borderRadius: "8px",
                  padding: "4px",
                }}
              >
                <div
                  onClick={() => {

                    setViewMode("full");
                  }}
                  style={{
                    flex: 1,
                    textAlign: "center",
                    padding: "8px 0",
                    fontSize: "11px",
                    fontWeight: "600",
                    cursor: "pointer",
                    borderRadius: "6px",
                    backgroundColor:
                      viewMode === "full" ? "#fff" : "transparent",
                    color: viewMode === "full" ? "#2C6AFF" : "#64748B",
                    boxShadow:
                      viewMode === "full"
                        ? "0 1px 3px rgba(0,0,0,0.1)"
                        : "none",
                  }}
                >
                  전체 보기
                </div>
                <div
                  onClick={() => setViewMode("compact")}
                  style={{
                    flex: 1,
                    textAlign: "center",
                    padding: "8px 0",
                    fontSize: "11px",
                    fontWeight: "600",
                    cursor: "pointer",
                    borderRadius: "6px",
                    backgroundColor:
                      viewMode === "compact" ? "#fff" : "transparent",
                    color: viewMode === "compact" ? "#2C6AFF" : "#64748B",
                    boxShadow:
                      viewMode === "compact"
                        ? "0 1px 3px rgba(0,0,0,0.1)"
                        : "none",
                  }}
                >
                  간략 보기
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                borderTop: "1px solid #F0F0F0",
                paddingTop: "24px",
              }}
            >
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: "bold",
                  color: "#818C9E",
                }}
              >
                {viewMode === "full" ? "전체 보기 기준" : "간략 보기 기준"}
              </div>
              <div
                onClick={() => setCompactCriteria("default")}
                style={{
                  border:
                    compactCriteria === "default"
                      ? "1px solid #2C6AFF"
                      : "1px solid #E2E8F0",
                  backgroundColor:
                    compactCriteria === "default" ? "#EFF6FF" : "#fff",
                  padding: "12px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    border:
                      compactCriteria === "default"
                        ? "4px solid #2C6AFF"
                        : "1px solid #CBD5E1",
                    backgroundColor: "#fff",
                  }}
                />
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "600",
                    color:
                      compactCriteria === "default" ? "#2C6AFF" : "#364153",
                  }}
                >
                  기본
                </span>
              </div>
              <div
                onClick={() => setCompactCriteria("custom")}
                style={{
                  border:
                    compactCriteria === "custom"
                      ? "1px solid #2C6AFF"
                      : "1px solid #E2E8F0",
                  backgroundColor:
                    compactCriteria === "custom" ? "#EFF6FF" : "#fff",
                  padding: "12px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    border:
                      compactCriteria === "custom"
                        ? "4px solid #2C6AFF"
                        : "1px solid #CBD5E1",
                    backgroundColor: "#fff",
                  }}
                />
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: "600",
                      color:
                        compactCriteria === "custom" ? "#2C6AFF" : "#364153",
                    }}
                  >
                    사용자 정의
                  </span>
                  <span
                    style={{
                      fontSize: "11px",
                      color:
                        compactCriteria === "custom" ? "#3B8BD4" : "#94A3B8",
                    }}
                  >
                    직접 선택
                  </span>
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                borderTop: "1px solid #F0F0F0",
                paddingTop: "24px",
              }}
            >
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: "bold",
                  color: "#818C9E",
                }}
              >
                전체 정상 체크 동작
              </div>
              <div
                onClick={() => setCheckAllBehavior("fill_empty")}
                style={{
                  border:
                    checkAllBehavior === "fill_empty"
                      ? "1px solid #2C6AFF"
                      : "1px solid #E2E8F0",
                  backgroundColor:
                    checkAllBehavior === "fill_empty" ? "#EFF6FF" : "#fff",
                  padding: "12px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    border:
                      checkAllBehavior === "fill_empty"
                        ? "4px solid #2C6AFF"
                        : "1px solid #CBD5E1",
                    backgroundColor: "#fff",
                  }}
                />
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "600",
                    color:
                      checkAllBehavior === "fill_empty" ? "#2C6AFF" : "#364153",
                  }}
                >
                  빈 항목만 채우기
                </span>
                <span
                  style={{
                    backgroundColor: "#3B8BD4",
                    color: "#fff",
                    fontSize: "11px",
                    fontWeight: "bold",
                    padding: "2px 6px",
                    borderRadius: "4px",
                  }}
                >
                  권장
                </span>
              </div>
              <div
                onClick={() => setCheckAllBehavior("overwrite")}
                style={{
                  border:
                    checkAllBehavior === "overwrite"
                      ? "1px solid #2C6AFF"
                      : "1px solid #E2E8F0",
                  backgroundColor:
                    checkAllBehavior === "overwrite" ? "#EFF6FF" : "#fff",
                  padding: "12px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    border:
                      checkAllBehavior === "overwrite"
                        ? "4px solid #2C6AFF"
                        : "1px solid #CBD5E1",
                    backgroundColor: "#fff",
                  }}
                />
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "600",
                    color:
                      checkAllBehavior === "overwrite" ? "#2C6AFF" : "#364153",
                  }}
                >
                  전체 덮어쓰기
                </span>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                borderTop: "1px solid #F0F0F0",
                paddingTop: "24px",
              }}
            >
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: "bold",
                  color: "#818C9E",
                }}
              >
                상병코드 입력 시 가이드 + 빈 항목 채우기
              </div>
              <div
                onClick={() => setAutoFillWithDisease(!autoFillWithDisease)}
                style={{
                  width: "44px",
                  height: "24px",
                  borderRadius: "12px",
                  cursor: "pointer",
                  position: "relative",
                  transition: "background-color 0.2s",
                  backgroundColor: autoFillWithDisease ? "#2C6AFF" : "#E2E8F0",
                }}
              >
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    position: "absolute",
                    top: "2px",
                    transition: "left 0.2s",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                    left: autoFillWithDisease ? "22px" : "2px",
                  }}
                />
              </div>
            </div>
          </div>

          <div
            style={{
              flex: 1,
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#FFFFFF",
              overflowY: "auto",
            }}
          >
            {(() => {
              const mockGroups = [
                {
                  group: "1. 정신 상태",
                  keys: ["Alertness", "Orientation", "Memory", "Speech"],
                },
                {
                  group: "2. 뇌신경 기능",
                  keys: ["Pupil", "EOM", "Facial", "Gag"],
                },
                {
                  group: "3. 운동 기능",
                  keys:
                    viewMode === "full"
                      ? [
                          "RUE",
                          "RLE",
                          "LUE",
                          "LLE",
                          "ShoulderLt",
                          "ShoulderRt",
                          "ElbowLt",
                          "ElbowRt",
                          "WristLt",
                          "WristRt",
                          "HipLt",
                          "HipRt",
                          "KneeLt",
                          "KneeRt",
                          "AnkleLt",
                          "AnkleRt",
                          "ToneUp",
                          "ToneLo",
                        ]
                      : ["RUE", "RLE", "LUE", "LLE", "ToneUp", "ToneLo"],
                },
                {
                  group: "4. 감각 기능",
                  keys: ["SensRUE", "SensRLE", "SensLUE", "SensLLE"],
                },
                {
                  group: "5. 반사",
                  keys:
                    viewMode === "full"
                      ? [
                          "BicepsLt",
                          "BicepsRt",
                          "TricepsLt",
                          "TricepsRt",
                          "PatellarLt",
                          "PatellarRt",
                          "AchillesLt",
                          "AchillesRt",
                          "Babinski",
                        ]
                      : ["DTR", "Babinski"],
                },
                {
                  group: "6. 소뇌 기능",
                  keys: ["Finger", "Heel", "Romberg"],
                },
                {
                  group: "7. 보행 및 자세",
                  keys: ["Walking", "Tandem"],
                },
              ];

              const totalItems = mockGroups.reduce(
                (acc, g) => acc + g.keys.length,
                0,
              );
              const selectedCount = mockGroups.reduce(
                (acc, g) =>
                  acc +
                  g.keys.filter(
                    (key) =>
                      (customIncludedKeys[viewMode] || {})[key] !== false,
                  ).length,
                0,
              );

              return (
                <>
                  {compactCriteria === "custom" && (
                    <div
                      style={{
                        backgroundColor: "#EFF6FF",
                        border: "1px solid #BFDBFE",
                        borderRadius: "8px",
                        padding: "12px 16px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "16px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          color: "#2C6AFF",
                          fontSize: "11px",
                          fontWeight: "600",
                        }}
                      >
                        <div
                          style={{
                            width: "16px",
                            height: "16px",
                            borderRadius: "50%",
                            border: "1px solid #2C6AFF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "11px",
                          }}
                        >
                          !
                        </div>
                        {viewMode === "full" ? "전체 보기" : "간략 보기"}에
                        포함할 항목을 오른쪽 체크박스로 선택하세요.
                      </div>
                      <div
                        style={{
                          color: "#2C6AFF",
                          fontSize: "11px",
                          fontWeight: "bold",
                        }}
                      >
                        {selectedCount} / {totalItems} 선택됨
                      </div>
                    </div>
                  )}

                  <div
                    style={{
                      backgroundColor: "#fff",
                      border: "1px solid #E2E8F0",
                      borderTop: "none",
                      position: "relative",
                    }}
                  >
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                      <thead>
                        <tr>
                          <th style={{ ...thStyle }}>중분류</th>
                          <th style={{ ...thStyle }}>
                            소분류 항목
                          </th>
                          <th style={{ ...thStyle }}>정상</th>
                          <th style={{ ...thStyle }}>비정상</th>
                          <th style={{ ...thStyle }}>점수/등급</th>
                          {compactCriteria === "custom" && (
                            <th style={thStyle}>포함</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {mockGroups.map((groupObj, gIdx) => (
                          <React.Fragment key={gIdx}>
                            {groupObj.keys.map((key, iIdx) => {
                              const itemLabel = tableLabels[key] || key;
                              const isCustomMode = compactCriteria === "custom";
                              const isIncluded =
                                (customIncludedKeys[viewMode] || {})[key] !==
                                false;
                              const isActive = !isCustomMode || isIncluded;

                              const itemBg = isCustomMode
                                ? isIncluded
                                  ? "#F0F6FE"
                                  : "#FFFFFF"
                                : "#FFFFFF";
                              const itemColor = isActive
                                ? "#818C9E"
                                : "#E3E8EF";
                              const mockBorderColor = isActive
                                ? "#CBD5E1"
                                : "#E3E8EF";
                              const mockBoxBg = isActive
                                ? "#F1F5F9"
                                : "#FFFFFF";
                              const fontWeight =
                                isCustomMode && isIncluded ? "600" : "400";

                              return (
                                <tr
                                  key={`${gIdx}-${iIdx}`}
                                  style={{ backgroundColor: itemBg }}
                                >
                                  {iIdx === 0 && (
                                    <td
                                      rowSpan={groupObj.keys.length}
                                      style={{
                                        ...tdStyle,
                                        color: "#364153",
                                        fontWeight: "600",
                                        backgroundColor: "#FFFFFF",
                                      }}
                                    >
                                      {groupObj.group}
                                    </td>
                                  )}
                                  <td
                                    style={{
                                      ...tdLeft,
                                      color: itemColor,
                                      fontWeight: fontWeight,
                                    }}
                                  >
                                    {itemLabel}
                                  </td>
                                  <td style={tdStyle}>
                                    <div
                                      style={{
                                        width: "16px",
                                        height: "16px",
                                        border: `1px solid ${mockBorderColor}`,
                                        borderRadius: "4px",
                                        margin: "0 auto",
                                      }}
                                    />
                                  </td>
                                  <td style={tdStyle}>
                                    <div
                                      style={{
                                        width: "16px",
                                        height: "16px",
                                        border: `1px solid ${mockBorderColor}`,
                                        borderRadius: "4px",
                                        margin: "0 auto",
                                      }}
                                    />
                                  </td>
                                  <td style={tdStyle}>
                                    <div
                                      style={{
                                        height: "16px",
                                        width: "80%",
                                        backgroundColor: mockBoxBg,
                                        border: isActive
                                          ? "none"
                                          : `1px solid #E3E8EF`,
                                        borderRadius: "4px",
                                        margin: "0 auto",
                                      }}
                                    />
                                  </td>
                                  {isCustomMode && (
                                    <td
                                      style={{
                                        ...tdStyle,
                                        backgroundColor: itemBg,
                                      }}
                                    >
                                      <label
                                        style={{
                                          display: "flex",
                                          justifyContent: "center",
                                          cursor: "pointer",
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isIncluded}
                                          onChange={(e) =>
                                            setCustomIncludedKeys((prev) => ({
                                              ...prev,
                                              [viewMode]: {
                                                ...(prev[viewMode] || {}),
                                                [key]: e.target.checked,
                                              },
                                            }))
                                          }
                                          style={{
                                            width: "16px",
                                            height: "16px",
                                            accentColor: "#2C6AFF",
                                            cursor: "pointer",
                                          }}
                                        />
                                      </label>
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #E2E8F0",
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
            backgroundColor: "#fff",
          }}
        >
          <Button
            onClick={handleCancelSettings}
            style={{
              borderRadius: "8px",
              padding: "0 24px",
              height: "40px",
              fontSize: "11px",
              fontWeight: "600",
              borderColor: "#E3E8EF",
              color: "#475569",
            }}
          >
            취소
          </Button>
          <Button
            type="primary"
            onClick={() => {
              localStorage.setItem("viewMode", JSON.stringify(viewMode));
              localStorage.setItem(
                "compactCriteria",
                JSON.stringify(compactCriteria),
              );
              localStorage.setItem(
                "checkAllBehavior",
                JSON.stringify(checkAllBehavior),
              );
              localStorage.setItem(
                "autoFillWithDisease",
                JSON.stringify(autoFillWithDisease),
              );
              localStorage.setItem(
                "customIncludedKeys",
                JSON.stringify(customIncludedKeys),
              );
              setSettingModalOpen(false);
            }}
            style={{
              borderRadius: "8px",
              padding: "0 24px",
              height: "40px",
              fontSize: "11px",
              fontWeight: "600",
              backgroundColor: "#2C6AFF",
            }}
          >
            적용
          </Button>
        </div>
      </Modal>

      <Modal
        open={copyModalOpen}
        onCancel={() => {
          setCopyModalOpen(false);
        }}
        closeIcon={false}
        footer={null}
        width={1000}
        centered
        styles={{ body: { padding: "10px 24px" } }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div
            style={{ fontSize: "18px", fontWeight: "bold", color: "#1E293B" }}
          >
            복사 미리보기
          </div>

          <Input.TextArea
            value={copyText}
            onChange={(e) => setCopyText(e.target.value)}
            rows={25}
            style={{
              backgroundColor: "#FFFFFF",
              color: "#364153",
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
              resize: "none",
            }}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              marginTop: "8px",
            }}
          >
            <Button
              onClick={() => setCopyModalOpen(false)}
              style={{
                borderRadius: "8px",
                padding: "0 24px",
                height: "40px",
                fontSize: "14px",
                fontWeight: "600",
                borderColor: "#E3E8EF",
                color: "#475569",
              }}
            >
              취소
            </Button>
            <Button
              type="primary"
              onClick={executeCopy}
              style={{
                borderRadius: "8px",
                padding: "0 24px",
                height: "40px",
                fontSize: "14px",
                fontWeight: "600",
                backgroundColor: "#2C6AFF",
              }}
            >
              복사
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        open={diseaseModalOpen}
        onCancel={() => setDiseaseModalOpen(false)}
        footer={null}
        width={1000}
        centered
        closeIcon={false}
        styles={{ body: { padding: "10px" } }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "010px" }}>
          <div>
            <div
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color: "#1E293B",
                marginBottom: "10px",
              }}
            >
              상병코드
            </div>
            <div
              style={{
                display: "flex",
                gap: "24px",
                color: "#64748B",
                fontSize: "13px",
              }}
            >
              <div>
                환자명{" "}
                <span
                  style={{
                    color: "#1E293B",
                    fontWeight: "bold",
                    marginLeft: "4px",
                  }}
                >
                  {patientName}
                </span>
              </div>
              <div>
                환자번호{" "}
                <span
                  style={{
                    color: "#1E293B",
                    fontWeight: "bold",
                    marginLeft: "4px",
                  }}
                >
                  {patientNo}
                </span>
              </div>
              <div>
                검사일{" "}
                <span
                  style={{
                    color: "#1E293B",
                    fontWeight: "bold",
                    marginLeft: "4px",
                  }}
                >
                  {selectedTest?.id
                    ? `${selectedTest.id.substring(0, 4)}.${selectedTest.id.substring(4, 6)}.${selectedTest.id.substring(6, 8)}`
                    : ""}
                </span>
              </div>
            </div>
          </div>

          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 16px",
                background: "#F8F9FA",
                borderBottom: "1px solid #F0F0F0",
                borderTop: "1px solid #F0F0F0",
                color: "#818C9E",
                fontSize: "12px",
                fontWeight: "600",
              }}
            >
              <span>상병코드</span>
              <span>MRI 여부</span>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                maxHeight: "700px",
                overflowY: "auto",
                padding: "0 16px",
              }}
            >
              {diseaseCodes.map((item) => (
                <div
                  key={item.code}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "16px 0",
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                    }}
                  >
                    <span
                      style={{
                        color: "#3B82F6",
                        fontWeight: "bold",
                        fontSize: "12px",
                        width: "80px",
                      }}
                    >
                      {item.code}
                    </span>
                    <span
                      style={{
                        color: "#364153",
                        fontSize: "13px",
                        fontWeight: "600",
                      }}
                    >
                      {item.desc}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    {["유", "무"].map((mriType) => {
                      const isSelected =
                        selectedDisease?.code === item.code &&
                        selectedDisease?.mri === mriType;
                      return (
                        <div
                          key={mriType}
                          onClick={() => {
                            if (isSelected) setSelectedDisease(null);
                            else
                              setSelectedDisease({
                                code: item.code,
                                mri: mriType,
                                desc: item.desc,
                              });
                          }}
                          style={{
                            width: "36px",
                            height: "28px",
                            borderRadius: "14px",
                            border: isSelected
                              ? "2px solid #2C6AFF"
                              : "1px solid #F0F0F0CC",
                            color: isSelected ? "#2C6AFF" : "#AAAAAACC",
                            background: isSelected ? "#F0F6FE" : "#FFFFFF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            fontWeight: "bold",
                            cursor: "pointer",
                            transition: "all 0.2s",
                          }}
                        >
                          {mriType}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              marginTop: "16px",
            }}
          >
            <Button
              onClick={() => setDiseaseModalOpen(false)}
              style={{
                borderRadius: "8px",
                padding: "0 24px",
                height: "40px",
                fontSize: "14px",
                fontWeight: "600",
                borderColor: "#E3E8EF",
                color: "#475569",
              }}
            >
              취소
            </Button>
            <Button
              type="primary"
              onClick={async () => {
                try {
                  if (selectedTest) {
                    const testRef = doc(
                      db,
                      "Accounts",
                      user.id,
                      "Patients",
                      selectedTest.patientId,
                      "Tests",
                      selectedTest.id,
                    );

                    await updateDoc(testRef, { disease: selectedDisease });

                    setSelectedTest((prev) => ({
                      ...prev,
                      disease: selectedDisease,
                    }));

                    setData((prevData) =>
                      prevData.map((dt) => {
                        if (dt.id !== selectedTest.patientId) return dt;
                        return {
                          ...dt,
                          tests: dt.tests.map((t) =>
                            t.id === selectedTest.id
                              ? { ...t, disease: selectedDisease }
                              : t,
                          ),
                        };
                      }),
                    );
                  }

                  if (autoFillWithDisease) {
                    const currentAllowedKeys = getVisibleKeys();
                    const guideResult = buildGuideResult();
                    setReportData((prevData) => {

                      const newData = { ...prevData, ...guideResult };

                      Object.keys(currentAllowedKeys).forEach((key) => {
                        const existing = newData[key];
                        if (
                          !existing ||
                          (!existing.normal &&
                            !existing.abnormal &&
                            !existing.score)
                        ) {
                          newData[key] = {
                            normal: true,
                            abnormal: false,
                            score: isJointKey(key)
                              ? "G0"
                              : isReflexKey(key) || key === "DTR"
                                ? "2+(정상)"
                                : currentAllowedKeys[key],
                            type: null,
                          };
                        }
                      });
                      return newData;
                    });
                    setIsAllChecked(true);
                  }

                  setDiseaseModalOpen(false);
                  showToast("저장되었습니다.");
                } catch (error) {
                  console.error("상병코드 저장 실패:", error);
                  alert("저장 중 오류가 발생했습니다.");
                }
              }}
              style={{
                borderRadius: "8px",
                padding: "0 24px",
                height: "40px",
                fontSize: "14px",
                fontWeight: "600",
                backgroundColor: "#2C6AFF",
              }}
            >
              저장
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        open={armModalOpen}
        onCancel={() => setArmModalOpen(false)}
        footer={null}
        width={1000}
        centered
        closeIcon={
          <span style={{ fontSize: "20px", color: "#364153" }}>×</span>
        }
        styles={{ body: { padding: "32px 24px" } }}
      >
        <div style={{ display: "flex", gap: "20px" }}>
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <div
              style={{
                border: "1px solid #5B7BE8CC",
                borderRadius: "12px",
                padding: "20px 24px",
                display: "flex",
                flexDirection: "column",
                background: "#F8FAFC",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "baseline", gap: "16px" }}
              >
                <span
                  style={{
                    color: "#818C9E",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  왼팔 각도
                </span>
                <span
                  style={{
                    color: "#5B7BE8",
                    fontSize: "28px",
                    fontWeight: "bold",
                  }}
                >
                  {leftAngleAvgData.toFixed(1)}°
                </span>
              </div>

              <div
                style={{
                  height: "1px",
                  backgroundColor: "#E2E8F0",
                  margin: "16px 0",
                }}
              />

              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <span
                  style={{
                    color: "#818C9E",
                    fontSize: "12px",
                    fontWeight: "600",
                    marginRight: "4px",
                    whiteSpace: "nowrap",
                  }}
                >
                  최대변동폭
                </span>
                <span
                  style={{
                    border: "1px solid #5B7BE8",
                    backgroundColor: "#fff",
                    color: "#5B7BE8",
                    padding: "6px 10px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                >
                  {(leftAngleMaxData - leftAngleMinData).toFixed(1)}°
                </span>
                <span
                  style={{
                    backgroundColor: "#E2E8F080",
                    color: "#5B7BE8",
                    padding: "6px 10px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                >
                  min {leftAngleMinData.toFixed(1)}°
                </span>
                <span
                  style={{
                    backgroundColor: "#E2E8F080",
                    color: "#5B7BE8",
                    padding: "6px 10px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                >
                  max {leftAngleMaxData.toFixed(1)}°
                </span>
              </div>
            </div>

            <div
              style={{
                border: "1px solid #E2E8F0",
                borderRadius: "12px",
                padding: "24px 20px",
                display: "flex",
                flexDirection: "column",
                height: "260px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "10px",
                  position: "relative",
                  zIndex: 10,
                }}
              >
                <span
                  style={{
                    backgroundColor: "#5B7BE81A",
                    color: "#5B7BE8CC",
                    padding: "6px 16px",
                    borderRadius: "20px",
                    fontSize: "13px",
                    fontWeight: "bold",
                  }}
                >
                  왼팔 각도
                </span>
              </div>
              <div style={{ position: "relative", flex: 1, marginTop: "10px" }}>
                <img
                  src={armImg}
                  alt="arm background"
                  style={{
                    position: "absolute",
                    left: "40px",
                    top: "10px",
                    bottom: "30px",
                    height: "calc(100% - 40px)",
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                />
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={leftArmChartData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                    style={{ zIndex: 2, position: "relative" }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={true}
                      horizontal={true}
                      stroke="#E2E8F0"
                    />
                    <XAxis
                      dataKey="time"
                      type="number"
                      domain={[0, chartMaxX]}
                      ticks={xTicks}
                      tickFormatter={(val) => `${val}s`}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 8, fill: "#BABABA", dy: 5 }}
                      height={30}
                    />
                    <YAxis
                      width={35}
                      domain={[0, 180]}
                      ticks={[0, 30, 60, 90, 120, 150, 180]}
                      tickFormatter={(val) => `${val}°`}
                      axisLine={false}
                      tickLine={false}
                      tick={{
                        textAnchor: "start",
                        dx: -20,
                        fontSize: 8,
                        fill: "#BABABA",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="angle"
                      stroke="#5B7BE8CC"
                      strokeWidth={2}
                      fill="#5B7BE81A"
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <div
              style={{
                border: "1px solid #3AAA7ACC",
                borderRadius: "12px",
                padding: "20px 24px",
                display: "flex",
                flexDirection: "column",
                background: "#F8FAFC",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "baseline", gap: "16px" }}
              >
                <span
                  style={{
                    color: "#818C9E",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  오른팔 각도
                </span>
                <span
                  style={{
                    color: "#3AAA7A",
                    fontSize: "28px",
                    fontWeight: "bold",
                  }}
                >
                  {rightAngleAvgData.toFixed(1)}°
                </span>
              </div>

              <div
                style={{
                  height: "1px",
                  backgroundColor: "#E2E8F0",
                  margin: "16px 0",
                }}
              />

              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <span
                  style={{
                    color: "#818C9E",
                    fontSize: "12px",
                    fontWeight: "600",
                    marginRight: "4px",
                    whiteSpace: "nowrap",
                  }}
                >
                  최대변동폭
                </span>
                <span
                  style={{
                    border: "1px solid #3AAA7ACC",
                    backgroundColor: "#fff",
                    color: "#3AAA7A",
                    padding: "6px 10px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                >
                  {(rightAngleMaxData - rightAngleMinData).toFixed(1)}°
                </span>
                <span
                  style={{
                    backgroundColor: "#E2E8F080",
                    color: "#3AAA7A",
                    padding: "6px 10px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                >
                  min {rightAngleMinData.toFixed(1)}°
                </span>
                <span
                  style={{
                    backgroundColor: "#E2E8F080",
                    color: "#3AAA7A",
                    padding: "6px 10px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                >
                  max {rightAngleMaxData.toFixed(1)}°
                </span>
              </div>
            </div>

            <div
              style={{
                border: "1px solid #E2E8F0",
                borderRadius: "12px",
                padding: "24px 20px",
                display: "flex",
                flexDirection: "column",
                height: "260px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "10px",
                  position: "relative",
                  zIndex: 10,
                }}
              >
                <span
                  style={{
                    backgroundColor: "#3AAA7A1A",
                    color: "#3AAA7ACC",
                    padding: "6px 16px",
                    borderRadius: "20px",
                    fontSize: "13px",
                    fontWeight: "bold",
                  }}
                >
                  오른팔 각도
                </span>
              </div>
              <div style={{ position: "relative", flex: 1, marginTop: "10px" }}>
                <img
                  src={armImg}
                  alt="arm background"
                  style={{
                    position: "absolute",
                    left: "40px",
                    top: "10px",
                    bottom: "30px",
                    height: "calc(100% - 40px)",
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                />
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={rightArmChartData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                    style={{ zIndex: 2, position: "relative" }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={true}
                      horizontal={true}
                      stroke="#E2E8F0"
                    />
                    <XAxis
                      dataKey="time"
                      type="number"
                      domain={[0, chartMaxX]}
                      ticks={xTicks}
                      tickFormatter={(val) => `${val}s`}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 8, fill: "#BABABA", dy: 5 }}
                      height={30}
                    />
                    <YAxis
                      width={35}
                      domain={[0, 180]}
                      ticks={[0, 30, 60, 90, 120, 150, 180]}
                      tickFormatter={(val) => `${val}°`}
                      axisLine={false}
                      tickLine={false}
                      tick={{
                        textAnchor: "start",
                        dx: -20,
                        fontSize: 8,
                        fill: "#BABABA",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="angle"
                      stroke="#3AAA7ACC"
                      strokeWidth={2}
                      fill="#3AAA7A1A"
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={reportModalOpen}
        onCancel={() => setReportModalOpen(false)}
        footer={null}
        width={800}
        centered
      >
        <div style={{ padding: "5px 0 10px 0" }}>
          <div
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: "#364153",
              marginBottom: "8px",
            }}
          >
            {activeExamType === "simple"
              ? "단순신경학적 소견서"
              : "일반신경학적 소견서"}
          </div>
          <div
            style={{
              display: "flex",
              gap: "16px",
              paddingBottom: "16px",
              borderBottom: "1px solid #E2E8F0",
            }}
          >
            <span style={{ fontSize: "11px", color: "#818C9E" }}>
              환자명{" "}
              <span
                style={{
                  fontSize: "12px",
                  color: "#364153",
                  fontWeight: "600",
                  marginLeft: "4px",
                }}
              >
                {patientName}
              </span>
            </span>
            <span style={{ fontSize: "11px", color: "#818C9E" }}>
              환자번호{" "}
              <span
                style={{
                  fontSize: "12px",
                  color: "#364153",
                  fontWeight: "600",
                  marginLeft: "4px",
                }}
              >
                {patientNo}
              </span>
            </span>
            <span style={{ fontSize: "11px", color: "#818C9E" }}>
              검사일{" "}
              <span
                style={{
                  fontSize: "12px",
                  color: "#364153",
                  fontWeight: "600",
                  marginLeft: "4px",
                }}
              >
                {selectedTest?.id
                  ? `${selectedTest.id.substring(0, 4)}.${selectedTest.id.substring(4, 6)}.${selectedTest.id.substring(6, 8)}`
                  : ""}
              </span>
            </span>
          </div>
        </div>

        <div
          style={{ maxHeight: "80vh", overflowY: "auto", paddingRight: "8px" }}
        >
          {renderReportModalContent()}
        </div>
      </Modal>
      <Modal
        open={previewOpen}
        onCancel={closeImagePreview}
        footer={null}
        width="80vw"
        centered
        destroyOnClose
        closeIcon={
          <span style={{ fontSize: "20px", color: "#364153" }}>×</span>
        }
        styles={{
          body: {
            padding: 0,
            height: "80vh",
            position: "relative",
            overflow: "hidden",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#f8fafc",
          },
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "20px",
            left: "20px",
            zIndex: 20,
          }}
        >
          <div
            onClick={handleClearAll}
            style={{
              padding: "8px 16px",
              backgroundColor: "#F0F6FE",
              color: "#3B82F6",
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            전체지우기
          </div>
        </div>

        <div
          style={{
            position: "absolute",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 20,
            display: "flex",
            gap: "4px",
            background: "#fff",
            border: "1px solid #E3E8EF",
            borderRadius: "8px",
            padding: "4px",
          }}
        >
          <Button
            type="text"
            size="small"
            onClick={zoomOutPreview}
            style={{ color: "#818C9E", fontWeight: "bold" }}
          >
            -
          </Button>
          <div
            style={{ width: "1px", background: "#E3E8EF", margin: "4px 0" }}
          />
          <Button
            type="text"
            size="small"
            onClick={resetPreviewZoom}
            style={{ color: "#818C9E", fontSize: "12px", fontWeight: "600" }}
          >
            원래 크기
          </Button>
          <div
            style={{ width: "1px", background: "#E3E8EF", margin: "4px 0" }}
          />
          <Button
            type="text"
            size="small"
            onClick={zoomInPreview}
            style={{ color: "#818C9E", fontWeight: "bold" }}
          >
            +
          </Button>
        </div>

        <div
          style={{
            position: "absolute",
            bottom: "24px",
            left: "24px",
            zIndex: 20,
            display: "flex",
            gap: "10px",
          }}
        >
          <div
            onClick={() => handleDrawModeChange("check")}
            style={{
              cursor: "pointer",
              padding: "8px 16px",
              borderRadius: "8px",
              border:
                drawMode === "check"
                  ? "1px solid #10B981"
                  : "1px solid #E3E8EF",
              backgroundColor: drawMode === "check" ? "#EFFCF5" : "#fff",
              color: drawMode === "check" ? "#10B981" : "#818C9E",
              fontWeight: "600",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              transition: "all 0.2s",
            }}
          >
            체크 표시
          </div>
          <div
            onClick={() => handleDrawModeChange("line")}
            style={{
              cursor: "pointer",
              padding: "8px 16px",
              borderRadius: "8px",
              border:
                drawMode === "line" ? "1px solid #3B82F6" : "1px solid #E3E8EF",
              backgroundColor: drawMode === "line" ? "#F0F6FE" : "#fff",
              color: drawMode === "line" ? "#3B82F6" : "#818C9E",
              fontWeight: "600",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              transition: "all 0.2s",
            }}
          >
            각도/거리 측정
          </div>
        </div>

        {toastMsg && (
          <div
            style={{
              position: "absolute",
              bottom: "28px",
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "#475569",
              color: "#fff",
              padding: "8px 24px",
              borderRadius: "24px",
              fontSize: "13px",
              fontWeight: "600",
              zIndex: 100,
              pointerEvents: "none",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                }}
              >
                {toastMsg}
              </div>
            )}

            <div
              style={{
                position: "absolute",
                bottom: "24px",
                right: "24px",
                zIndex: 20,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: "36px",
                  boxSizing: "border-box",
                  gap: "14px",
                  padding: "0 16px",
                  borderRadius: "100px",
                  border: "1px solid #E3E8EF",
                  backgroundColor: "#fff",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                }}
              >
                <div
                  onClick={handleUndo}
                  style={{
                    cursor: history.length > 0 ? "pointer" : "not-allowed",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={history.length > 0 ? undoSelectedImg : undoUnSelectedImg}
                    alt="undo"
                    draggable={false}
                    style={{
                      width: "20px",
                      height: "20px",
                      userSelect: "none",
                      opacity: history.length > 0 ? 1 : 0.6,
                    }}
                  />
                </div>

                <div
                  style={{
                    width: "1px",
                    height: "16px",
                    backgroundColor: "#E3E8EF",
                  }}
                />

                <div
                  onClick={handleRedo}
                  style={{
                    cursor: redoStack.length > 0 ? "pointer" : "not-allowed",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={redoStack.length > 0 ? redoSelectedImg : redoUnSelectedImg}
                    alt="redo"
                    draggable={false}
                    style={{
                      width: "20px",
                      height: "20px",
                      userSelect: "none",
                      opacity: redoStack.length > 0 ? 1 : 0.6,
                    }}
                  />
                </div>
              </div>
            </div>

            <div
              style={{
                width: "100%",
                height: "100%",
                overflow: "auto",
                display: "flex",
                justifyContent: previewScale > 1 ? "flex-start" : "center",
                alignItems: previewScale > 1 ? "flex-start" : "center",
                padding: previewScale > 1 ? "40px" : "0",
              }}
            >
              {previewImage && (
                <div
                  style={{
                    transform: `scale(${previewScale})`,
                    transformOrigin:
                      previewScale > 1 ? "top left" : "center center",
                    transition: "transform 0.2s ease",
                    position: "relative",
                    display: "inline-block",
                    cursor: drawMode === "check" ? "pointer" : "crosshair",
                  }}
                  onMouseDown={handleImageMouseDown}
                  onMouseMove={handleImageMouseMove}
                  onMouseUp={handleImageMouseUp}
                  onMouseLeave={handleImageMouseUp}
                >
                  <img
                    key={previewImage}
                    src={previewImage}
                    alt="preview"
                    draggable={false}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      userSelect: "none",
                      display: "block",
                    }}
                  />

                  <svg
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      zIndex: 5,
                      pointerEvents: "none",
                    }}
                  >
                    {(imageLines[previewImage] || []).map((line) => {
                      const { cm, degree } = calculateLineData(
                        line.startX,
                        line.startY,
                        line.endX,
                        line.endY,
                      );
                      const dx = line.endX - line.startX;
                      const dy = line.endY - line.startY;
                      const length = Math.sqrt(dx * dx + dy * dy);

                      const ux = length > 0 ? dx / length : 0;
                      const uy = length > 0 ? dy / length : 0;

                      const offsetDegree = 25;
                      const offsetCm = 45;

                      const label1X = line.startX - ux * offsetDegree;
                      const label1Y = line.startY - uy * offsetDegree;

                      const label2X = line.endX + ux * offsetCm;
                      const label2Y = line.endY + uy * offsetCm;

                      return (
                        <g
                          key={`line-${line.id}`}
                          style={{ pointerEvents: "auto", cursor: "pointer" }}
                          onClick={(e) => handleLineClick(e, line.id)}
                        >
                          <line
                            x1={line.startX}
                            y1={line.startY}
                            x2={line.endX}
                            y2={line.endY}
                            stroke="transparent"
                            strokeWidth="15"
                          />
                          <line
                            x1={line.startX}
                            y1={line.startY}
                            x2={line.endX}
                            y2={line.endY}
                            stroke="#3B82F6"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          <circle
                            cx={line.startX}
                            cy={line.startY}
                            r="2"
                            fill="#3B82F6"
                          />
                          <circle
                            cx={line.endX}
                            cy={line.endY}
                            r="2"
                            fill="#3B82F6"
                          />

                          <foreignObject
                            x={label1X - 30}
                            y={label1Y - 15}
                            width="60"
                            height="30"
                            style={{ pointerEvents: "none", overflow: "visible" }}
                          >
                            <div
                              style={{
                                background: "#fff",
                                border: "1px solid #E3E8EF",
                                borderRadius: "12px",
                                padding: "2px 8px",
                                display: "inline-block",
                                fontSize: "9px",
                                color: "#818C9E",
                                whiteSpace: "nowrap",
                                fontWeight: "bold",
                              }}
                            >
                              {degree}°
                            </div>
                          </foreignObject>

                          <foreignObject
                            x={label2X - 30}
                            y={label2Y - 15}
                            width="60"
                            height="30"
                            style={{ pointerEvents: "none", overflow: "visible" }}
                          >
                            <div
                              style={{
                                background: "#fff",
                                border: "1px solid #E3E8EF",
                                borderRadius: "12px",
                                padding: "2px 8px",
                                display: "inline-block",
                                fontSize: "9px",
                                color: "#818C9E",
                                whiteSpace: "nowrap",
                                fontWeight: "bold",
                              }}
                            >
                              {cm}cm
                            </div>
                          </foreignObject>
                        </g>
                      );
                    })}

                    {drawingLine &&
                      (() => {
                        const dx = drawingLine.endX - drawingLine.startX;
                        const dy = drawingLine.endY - drawingLine.startY;
                        const length = Math.sqrt(dx * dx + dy * dy);
                        if (length < 5) return null;
                        const { cm, degree } = calculateLineData(
                          drawingLine.startX,
                          drawingLine.startY,
                          drawingLine.endX,
                          drawingLine.endY,
                        );

                        const ux = length > 0 ? dx / length : 0;
                        const uy = length > 0 ? dy / length : 0;

                        const offsetDegree = 25;
                        const offsetCm = 45;

                        const label1X = drawingLine.startX - ux * offsetDegree;
                        const label1Y = drawingLine.startY - uy * offsetDegree;

                        const label2X = drawingLine.endX + ux * offsetCm;
                        const label2Y = drawingLine.endY + uy * offsetCm;

                        return (
                          <g>
                            <line
                              x1={drawingLine.startX}
                              y1={drawingLine.startY}
                              x2={drawingLine.endX}
                              y2={drawingLine.endY}
                              stroke="#3B82F6"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeDasharray="4 4"
                            />
                            <circle
                              cx={drawingLine.startX}
                              cy={drawingLine.startY}
                              r="2"
                              fill="#3B82F6"
                            />
                            <circle
                              cx={drawingLine.endX}
                              cy={drawingLine.endY}
                              r="2"
                              fill="#3B82F6"
                            />

                            <foreignObject
                              x={label1X - 30}
                              y={label1Y - 15}
                              width="60"
                              height="30"
                              style={{ overflow: "visible" }}
                            >
                              <div
                                style={{
                                  background: "#fff",
                                  border: "1px solid #E3E8EF",
                                  borderRadius: "12px",
                                  padding: "2px 8px",
                                  display: "inline-block",
                                  fontSize: "9px",
                                  color: "#818C9E",
                                  whiteSpace: "nowrap",
                                  fontWeight: "bold",
                                }}
                              >
                                {degree}°
                              </div>
                            </foreignObject>

                            <foreignObject
                              x={label2X - 30}
                              y={label2Y - 15}
                              width="60"
                              height="30"
                              style={{ overflow: "visible" }}
                            >
                              <div
                                style={{
                                  background: "#fff",
                                  border: "1px solid #E3E8EF",
                                  borderRadius: "12px",
                                  padding: "2px 8px",
                                  display: "inline-block",
                                  fontSize: "9px",
                                  color: "#818C9E",
                                  whiteSpace: "nowrap",
                                  fontWeight: "bold",
                                }}
                              >
                                {cm}cm
                              </div>
                            </foreignObject>
                          </g>
                        );
                      })()}
                  </svg>

                  {(imageMarkers[previewImage] || []).map((marker) => (
                    <div
                      key={marker.id}
                      onClick={(e) => handleMarkerClick(e, marker.id)}
                      style={{
                        position: "absolute",
                        left: marker.x,
                        top: marker.y,
                        transform: "translate(-50%, -50%)",
                        zIndex: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        filter: "drop-shadow(1px 1px 2px rgba(0,0,0,0.3))",
                      }}
                    >
                      <img
                        src={checkedImg}
                        alt="check marker"
                        draggable={false}
                        style={{
                          width: "14px",
                          height: "14px",
                          pointerEvents: "none",
                          userSelect: "none",
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Modal>
        </Layout>
      );
    };

export default Customer;
